// Effects lane: implements FxApi (plus the elemental / hazard / gate extras below). Everything is
// built from five GPU systems:
//   Particles (sparks, lit smoke, fire, dust, embers, shards)  1 draw
//   Cards     (flashes, glows, tracers, rings, decals, arcs)    1 draw
//   Debris    (3D tumbling panels, bolts, toast, rocks)         1 draw
//   Numbers   (damage numbers)                                  1 draw
//   Bullets   (orbs/streaks + bomb + boulder w/ ink)            up to 5 draws
//   WaterFx   (splashes, wakes, rooster tails, dimples, steam)  1 draw (fx/water.ts)
// = at most 10 draw calls for every effect on screen, no allocations per frame.
//
// Round 2 look: smoke is lit by the sun from the FX atlas (cream top, teal shadow, gold rim) and
// erodes like worn ink; explosions are a white flash, a fireball that cools into a rolling lit
// smoke column, a dust skirt, sparks, embers, debris and a scorch that lingers; soft atlas glows
// fake the bloom the post pass doesn't have.
//
// `screen` carries post-pass feedback (flash, chroma kick, hurt, camera shake) that the game
// copies into PostPipeline uniforms / the camera each frame; it decays here.

import * as THREE from 'three';
import type { FxApi, ViewCtx } from '../contracts';
import { PAL } from '../palette';
import { ENEMIES } from '../../../sim/defs';
import { Particles, PShape, PFlag } from './particles';
import { Cards, CMode, COrient, CFlag } from './cards';
import { Debris } from './debris';
import { Numbers } from './numbers';
import { Bullets } from './bullets';
import { linRGB } from './gpu';
import { WaterFx } from './water';

const C = {
  white: linRGB(0xfff6e4),
  sun: linRGB(PAL.sunCore),
  hazeLight: linRGB(PAL.hazeLight),
  amber: linRGB(PAL.primeAmber),
  brass: linRGB(PAL.brass),
  brassTrim: linRGB(PAL.brassTrim),
  ink: linRGB(PAL.inkDark),
  dirt: linRGB(PAL.dirt),
  tan: linRGB(PAL.tan),
  cobble: linRGB(PAL.cobble),
  grassGold: linRGB(PAL.grassGold),
  teal: linRGB(PAL.shadowTeal),
  fireCore: linRGB(PAL.thrusterCore),
  fire: linRGB(PAL.thrusterRing),
  red: linRGB(PAL.toasterRed),
  rust: linRGB(PAL.rust),
  hurt: linRGB(PAL.hurtRed),
  laser: linRGB(PAL.laserRed),
  magenta: linRGB(PAL.primeMagenta),
  violet: linRGB(0xb98cff),
  violetDeep: linRGB(0x6a3fd0),
  blue: linRGB(PAL.shieldBlue),
  chrome: linRGB(PAL.chrome),
  steel: linRGB(PAL.steel),
  gunmetal: linRGB(PAL.gunmetal),
  cream: linRGB(PAL.warmCream),
  toast: linRGB(0xd9a35a),
  // the whiskey bottle (heal lane): amber glass, its bright edge, the thick base; the dregs and their stain
  glass: linRGB(0xc8822e), glassHi: linRGB(0xf6d79a), glassDeep: linRGB(0x7a4416),
  whiskey: linRGB(0xb8641c), whiskeyDark: linRGB(0x6e3510), whiskeyStain: linRGB(0x8a521c),
  // lit-smoke albedos (the atlas shading does the light; these are the inks)
  smokeDark: linRGB(0x4b3f33),
  smokeMid: linRGB(0x7a6a56),
  smokeLight: linRGB(0xc9bca2),
  plaster: linRGB(0xe8dcc4),
  siding: linRGB(0x3a6f72),
  // sunlit plaster-and-timber dust from a falling building
  dustOchre: linRGB(0xd6ae72),
  sawdust: linRGB(0xb09a7c),
  // a burning building's column: warm umber, not soot-black
  smokeUmber: linRGB(0x5e4029),
  soot: linRGB(0x1e1912),
  ember: linRGB(0xff7a2a),
  flameHot: linRGB(0xffb83a),
  flameCool: linRGB(0xe8461c),
  // elements: each owns a hue family outside the gold/teal world and away from enemy red
  // olive-mint, printed: no neon lime (it read as UI)
  acid: linRGB(0xa9c477),
  acidDeep: linRGB(0x46602f),
  acidFume: linRGB(0xa4b48c),
  acidFoam: linRGB(0xe2efc4),
  // engineer gas: a sickly mustard-olive cloud, distinct from the acid pool's mint
  gas: linRGB(0xc9bf6a),
  gasDeep: linRGB(0x6f6a36),
  // disruptor aura: shield-blue with a cyan heal glint
  aura: linRGB(0x7fd8ff),
  shock: linRGB(0xa8f4ff),
  shockDeep: linRGB(0x2f7cc8),
  ice: linRGB(0xdff6ff),
  iceDeep: linRGB(0x6fa8c8),
  water: linRGB(0x7fc4d8),
  waterDeep: linRGB(0x1f4a5a),
  steam: linRGB(0xf4efe6),
};

/** Ink colours each appliance comes apart into (art guide §6). */
export const INKS: (readonly [number, number, number])[][] = [
  // warm/earth inks only: no blue or cyan shards (critic 2) -- they read as ice/shield FX, not robot
  [linRGB(PAL.toasterRed), linRGB(PAL.plum), linRGB(PAL.gunmetal)], // swarmer: toaster
  [linRGB(PAL.paperCream), linRGB(PAL.safetyOrange), linRGB(PAL.inkDark)], // shooter: fan
  [linRGB(PAL.avocado), linRGB(PAL.rust), linRGB(PAL.inkDark)], // brute: boiler
  [linRGB(PAL.olive), linRGB(PAL.army), linRGB(PAL.gunmetal)], // sniper: canister
  [linRGB(PAL.warmCream), linRGB(PAL.washerBand), linRGB(PAL.brick)], // bomber: washer
  [linRGB(PAL.steel), linRGB(PAL.gunmetal), linRGB(PAL.rust)], // metal brute
  [linRGB(PAL.plum), linRGB(PAL.brass), linRGB(PAL.inkDark)], // disruptor
  [linRGB(PAL.bulldozer), linRGB(PAL.gunmetal), linRGB(PAL.inkDark)], // engineer
];

/** Detonation flavour colours per class: [core, edge, flash rgb (sRGB 0..1 for the post pass)]. */
const DET = [
  { core: C.sun, edge: C.amber, flash: [1.0, 0.85, 0.45] },
  { core: C.fireCore, edge: C.fire, flash: [1.0, 0.6, 0.3] },
  { core: linRGB(0xf2e6ff), edge: C.violet, flash: [0.8, 0.6, 1.0] },
  { core: C.hazeLight, edge: C.amber, flash: [1.0, 0.8, 0.55] },
];

/** Element ids for element() / hazard() / reaction(). */
export const enum El { Fire = 0, Acid = 1, Shock = 2, Ice = 3, Water = 4, Steam = 5, Tornado = 6, Gas = 7 }

const rnd = (a: number, b: number) => a + Math.random() * (b - a);

const MAX_HAZ = 32;
const MAX_AURA = 16;
/** Gameplay markers float just above the meadow grass (a holo ring), hazard footprints sit in it. */
const MARK_LIFT = 0.3;
const DECAL_LIFT = 0.15;

export class Fx implements FxApi {
  readonly particles: Particles;
  readonly cards: Cards;
  readonly debris: Debris;
  readonly numbers: Numbers;
  readonly bulletView: Bullets;
  /** everything cowboys do to water (fx/water.ts): 1 draw */
  readonly water: WaterFx;
  /** post/camera feedback; copy into post uniforms each frame (uFlash, uFlashColor, uChroma, uHurt) */
  readonly screen = { flash: 0, flashR: 1, flashG: 0.9, flashB: 0.7, chroma: 0, hurt: 0, shake: 0 };

  private time = 0;
  private mul: number;
  private q: number;
  private world: ViewCtx['world'];
  /** the paint of the house wall at a point (bound by the world view): a burst throws its own colour */
  wallPaint?: (x: number, y: number, z: number, nx: number, nz: number) => readonly [number, number, number] | null;
  /** the posse warped to a new island */
  setWorld(w: ViewCtx['world']) { this.world = w; }
  private renderer: THREE.WebGLRenderer | null;
  private markerSlot = new Map<number, number>();
  private markerT0 = new Map<number, number>();
  // scratch random direction
  private dx = 0; private dy = 0; private dz = 0;
  private tmp2 = new THREE.Vector2();
  private tmp = new THREE.Vector3();
  private camX = 0; private camY = 0; private camZ = 0;
  // enemy-bullet bookkeeping: muzzle pops for new bullets, trails for bombs/boulders
  private seenIds = new Int32Array(4096).fill(-1);
  private lastTrail = 0;
  // persistent hazards (struct of arrays, fixed capacity)
  private hId = new Int32Array(MAX_HAZ).fill(-1);
  private hEl = new Int8Array(MAX_HAZ);
  private hX = new Float32Array(MAX_HAZ); private hY = new Float32Array(MAX_HAZ); private hZ = new Float32Array(MAX_HAZ);
  private hR = new Float32Array(MAX_HAZ);
  private hEnd = new Float32Array(MAX_HAZ);
  private hAcc = new Float32Array(MAX_HAZ);
  private hAcc2 = new Float32Array(MAX_HAZ);
  private hDecal = new Int32Array(MAX_HAZ).fill(-1);
  private hDecalT0 = new Float32Array(MAX_HAZ);
  // warp gate
  private gX = 0; private gY = 0; private gZ = 0; private gR = 0; private gNx = 1; private gNz = 0;
  private aId = new Int32Array(MAX_AURA).fill(-1); private aLast = new Float32Array(MAX_AURA).fill(-1e9);
  private aPrev = new Float32Array(MAX_AURA); private aAcc = new Float32Array(MAX_AURA); private aPulse = new Float32Array(MAX_AURA);
  private beamLast = -1e9;
  // in-flight grenades (ice / magnetic / gas): their last place, so the burst fires where they land
  private nId = new Int32Array(32).fill(-1); private nKind = new Int8Array(32); private nX = new Float32Array(32);
  private nY = new Float32Array(32); private nZ = new Float32Array(32); private nSeen = new Uint8Array(32);
  private gCharge = 0; private gAcc = 0; private gAcc2 = 0; private gAcc3 = 0; private gAcc4 = 1; private gOn = false;

  constructor(ctx: ViewCtx) {
    const q = this.q = Math.max(0, Math.min(3, ctx.quality | 0));
    this.mul = [0.45, 0.75, 1, 1.2][q];
    this.world = ctx.world;
    this.renderer = ctx.renderer ?? null;
    this.particles = new Particles([2048, 4096, 8192, 12288][q]);
    this.cards = new Cards([512, 768, 1024, 1536][q]);
    this.debris = new Debris([128, 256, 512, 768][q]);
    this.numbers = new Numbers(256);
    this.bulletView = new Bullets(512);
    this.water = new WaterFx(q);
    ctx.scene.add(this.debris.mesh, this.bulletView.group, this.particles.mesh, this.cards.mesh, this.numbers.mesh, this.water.mesh);
  }

  // ---------------------------------------------------------------- helpers
  private n(k: number) { return Math.max(1, Math.round(k * this.mul)); }
  /** for the class-move lane (fx/classfx.ts): particle budget scale, the floor under a point, and the screen kick */
  get budget() { return this.mul; }
  groundAt(x: number, y: number, z: number) { return this.ground(x, y, z); }
  pulse(flash: number, rgb: readonly number[] | null, chroma = 0, shake = 0) { this.kick(flash, rgb, chroma, shake); }
  private ground(x: number, y: number, z: number) {
    const w = this.world as unknown as { support?: (x: number, z: number, y: number) => number } | undefined;
    const g = w && w.support ? w.support(x, z, y + 0.4) : 0;
    return g < -1e6 ? y - 60 : g;
  }
  /** The water surface at (x,z) if (x,y,z) is at it (within `tol` above, or under it), else NaN: things that land in a pond splash. */
  private wet(x: number, y: number, z: number, tol = 0.6) {
    const w = this.world as unknown as { waterAt?: (x: number, z: number) => number } | undefined;
    const wy = w && w.waterAt ? w.waterAt(x, z) : -1e9;
    return wy > -1e6 && y < wy + tol ? wy : NaN;
  }
  /** random unit vector into dx,dy,dz; upward bias 0..1 */
  private dir(up = 0) {
    let x, y, z, l;
    do { x = Math.random() * 2 - 1; y = Math.random() * 2 - 1; z = Math.random() * 2 - 1; l = x * x + y * y + z * z; } while (l > 1 || l < 1e-4);
    l = Math.sqrt(l);
    x /= l; y /= l; z /= l;
    y = y * (1 - up) + up * Math.abs(y) + up * 0.5;
    l = Math.sqrt(x * x + y * y + z * z);
    this.dx = x / l; this.dy = y / l; this.dz = z / l;
  }
  /** Screen-size compensation for small effects: 1 up close, grows with range so hits read at 30 m. */
  private ds(x: number, y: number, z: number) {
    const d = Math.hypot(x - this.camX, y - this.camY, z - this.camZ);
    return Math.min(2.6, Math.max(1, d / 7));
  }
  private kick(flash: number, rgb: readonly number[] | null, chroma = 0, shake = 0) {
    const s = this.screen;
    if (flash > s.flash) { s.flash = flash; if (rgb) { s.flashR = rgb[0]; s.flashG = rgb[1]; s.flashB = rgb[2]; } }
    s.chroma = Math.max(s.chroma, chroma);
    s.shake = Math.max(s.shake, shake);
  }
  private sparks(x: number, y: number, z: number, count: number, speed: number, col: readonly number[] | Float32Array, k: number, size = 0.06, life = 0.4, up = 0.2, grav = 12, floor = -1e5, flags: number = PFlag.Add) {
    const P = this.particles;
    for (let i = 0; i < count; i++) {
      this.dir(up);
      const s = speed * rnd(0.5, 1.15);
      P.spawn(x, y, z, this.dx * s, this.dy * s, this.dz * s, life * rnd(0.6, 1.2), 0, grav, 1.6, floor,
        PShape.Spark, flags | PFlag.Stretch, size, size * 0.5, 0, col as any, 1, col as any, 0.6, k, k * 0.6);
    }
  }
  /** Lit atlas smoke. hot = starts as a fireball that cools into smoke. */
  private puffs(x: number, y: number, z: number, count: number, spread: number, size0: number, size1: number, life: number, delay: number,
    rise: number, albedo: readonly number[], hot = false, alpha = 1, drag = 2.2, floor = -1e5, up = 0.5, flags = 0) {
    const P = this.particles;
    for (let i = 0; i < count; i++) {
      this.dir(up);
      const s = spread * rnd(0.35, 1);
      P.spawn(x + this.dx * size0 * 0.4, y + this.dy * size0 * 0.3, z + this.dz * size0 * 0.4, this.dx * s, this.dy * s * 0.6 + rise, this.dz * s,
        life * rnd(0.75, 1.25), delay * rnd(0.5, 1.5), -rise * 0.15, drag, floor,
        PShape.Puff, (hot ? PFlag.Hot : 0) | flags, size0 * rnd(0.75, 1.2), size1 * rnd(0.8, 1.2), rnd(-0.5, 0.5), albedo as any, alpha, albedo as any, alpha, 1, 0.92);
    }
  }
  /** Ground-hugging lit dust pushed out radially. */
  private dustRing(x: number, y: number, z: number, count: number, speed: number, size0: number, size1: number, life: number, floor: number, delay = 0, albedo: readonly number[] = C.tan) {
    const P = this.particles;
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2, s = speed * rnd(0.5, 1.1);
      const ca = Math.cos(a), sa = Math.sin(a);
      P.spawn(x + ca * size0 * 0.5, y + size0 * 0.3, z + sa * size0 * 0.5, ca * s, rnd(0.3, 1.2), sa * s, life * rnd(0.7, 1.3), delay * rnd(0.8, 1.2), -0.3, 3, floor,
        PShape.Puff, 0, size0 * rnd(0.7, 1.2), size1 * rnd(0.8, 1.2), rnd(-0.4, 0.4), (Math.random() < 0.5 ? albedo : C.cobble) as any, 1, albedo as any, 1, 1, 0.95);
    }
  }
  /** Upright flame tongues. */
  private flames(x: number, y: number, z: number, count: number, rad: number, size: number, life: number, delay: number, rise = 1.5, hot: readonly number[] = C.flameHot, cool: readonly number[] = C.flameCool, k = 1.5) {
    const P = this.particles;
    for (let i = 0; i < count; i++) {
      const a = Math.random() * 6.283, rr = rad * Math.sqrt(Math.random());
      P.spawn(x + Math.cos(a) * rr, y + size * 0.4, z + Math.sin(a) * rr, rnd(-0.2, 0.2), rise * rnd(0.6, 1.3), rnd(-0.2, 0.2), life * rnd(0.7, 1.3), delay * Math.random(), -0.5, 2, -1e5,
        PShape.Fire, PFlag.Add | PFlag.Up, size * rnd(0.7, 1.1), size * rnd(0.85, 1.3), 0, hot as any, 1, cool as any, 0.9, k, k);
    }
  }
  private embers(x: number, y: number, z: number, count: number, spread: number, life: number, col: readonly number[] = C.ember, rise = 1.2) {
    const P = this.particles;
    for (let i = 0; i < count; i++) {
      this.dir(0.7);
      P.spawn(x, y, z, this.dx * spread, this.dy * spread + rise, this.dz * spread, life * rnd(0.6, 1.3), rnd(0, 0.3), -0.3, 1.2, -1e5,
        PShape.Spark, PFlag.Add, 0.035, 0.02, 0, col as any, 1, col as any, 0.3, 2.2, 1.4);
    }
  }
  private soft(x: number, y: number, z: number, size: number, life: number, hot: readonly number[], edge: readonly number[], k: number, delay = 0) {
    this.cards.spawn(CMode.Soft, COrient.Billboard, x, y, z, 0, 0, 0, life, delay, size * 0.85, size, hot as any, 1, edge as any, CFlag.Add, 0, 0, 0, 0, k);
  }
  private flare(x: number, y: number, z: number, size: number, life: number, hot: readonly number[], edge: readonly number[], k: number, spikes = 0) {
    this.cards.spawn(CMode.Flare, COrient.Billboard, x, y, z, 0, 0, 0, life, 0, size, size * 0.6, hot as any, 1, edge as any, CFlag.Add, spikes, 0, 0, Math.random() * 6.28, k);
  }
  /** a short smoke trail behind a debris piece: puffs dropped where the piece will be (same ballistics as Debris) */
  private debrisTrail(x: number, y: number, z: number, vx: number, vy: number, vz: number, grav: number, drag: number, floor: number, n: number, size: number) {
    const k = Math.max(drag, 1e-3);
    for (let i = 1; i <= n; i++) {
      const t = i * 0.07;
      const e = (1 - Math.exp(-k * t)) / k, gk = grav / k;
      const px = x + vx * e, pz = z + vz * e, py = y + (vy + gk) * e - gk * t;
      if (py < floor) break;
      this.particles.spawn(px, py, pz, 0, 0.4, 0, rnd(0.5, 0.8), t, -0.2, 2, floor, PShape.Puff, PFlag.Soft, size * (0.5 + 0.1 * i), size * (1.4 + 0.2 * i), rnd(-1, 1),
        C.smokeMid, 0.75, C.smokeLight, 0.6, 1, 1);
    }
  }
  /** fake light: a warm additive disc laid on the ground under fire (no real lights in the FX lane) */
  private groundGlow(x: number, gy: number, z: number, r: number, life: number, hot: readonly number[], edge: readonly number[], k: number, delay = 0) {
    // darken-then-warm (cards Soft p0 = 1): a dark ring with an HDR core, k scaled into bloom range
    this.cards.spawn(CMode.Soft, COrient.Ground, x, gy + 0.08, z, 0, 0, 0, life, delay, r * 0.9, r, hot as any, 1, edge as any, 0, 1, 0, 0, 0, k * 2.6);
  }
  private scorch(x: number, y: number, z: number, r: number, life = 7) {
    this.cards.spawn(CMode.Decal, COrient.Ground, x, y, z, 0, 0, 0, life * (0.6 + 0.4 * this.mul), 0, r * 0.8, r, C.soot, 0.85, C.ember, 0, 0, 0, 0, Math.random() * 6.28, 1);
  }

  // ---------------------------------------------------------------- guns
  muzzle(pos: THREE.Vector3, dir: THREE.Vector3, cls: number, local: boolean) {
    const k = local ? 1 : 0.85;
    const x = pos.x + dir.x * 0.06, y = pos.y + dir.y * 0.06, z = pos.z + dir.z * 0.06;
    const Cd = this.cards, rot = Math.random() * 6.28;
    const fx = x + dir.x * 0.25, fy = y + dir.y * 0.25, fz = z + dir.z * 0.25;
    // shared read at the chase camera: a 4-frame cream-gold star ~0.6 m across, pushed a little
    // ahead of the barrel so the cowboy's own body doesn't hide it, and a warm tint on the ground
    {
      const big = cls === 0 ? 1.15 : cls === 2 ? 0.7 : cls === 3 ? 1 : 0.85;
      const sx = x + dir.x * 0.35, sy = y + dir.y * 0.35, sz = z + dir.z * 0.35;
      Cd.spawn(CMode.Flash, COrient.Billboard, sx, sy, sz, 0, 0, 0, 0.066, 0, 0.26 * big, 0.36 * big, C.cream, 1, cls === 2 ? C.violet : C.sun, 0, 6, Math.random(), 0, rot, 3.2);
      this.soft(sx, sy, sz, 0.8 * big, 0.08, C.sun, C.amber, 0.9 * k);
      const g = this.ground(x, y, z);
      if (y - g < 2.5) this.groundGlow(x + dir.x * 0.8, g, z + dir.z * 0.8, 1.4 * big, 0.1, C.sun, C.amber, 0.7 * k);
    }
    switch (cls) {
      case 0: // Big Iron: big brass-gold star, black-powder puff
        Cd.spawn(CMode.Flash, COrient.Billboard, x, y, z, 0, 0, 0, 0.075, 0, 0.24, 0.34, C.white, 1, C.amber, CFlag.Add, 6, Math.random(), 0, rot, 2.2 * k);
        Cd.spawn(CMode.Muzzle, COrient.Axis, x, y, z, dir.x, dir.y, dir.z, 0.07, 0, 0.95, 0.34, C.sun, 1, C.amber, CFlag.Add, 0, Math.random(), 0, 0, 2 * k);
        this.soft(fx, fy, fz, 0.9, 0.09, C.sun, C.amber, 0.9 * k);
        this.puffs(x + dir.x * 0.35, y + dir.y * 0.35, z + dir.z * 0.35, this.n(3), 0.9, 0.12, 0.45, 0.8, 0.02, 0.45, C.smokeLight, false, 0.85);
        this.sparks(x, y, z, this.n(3), 7, C.brass, 2, 0.04, 0.22, 0, 6);
        break;
      case 1: // Pulse Rifle: tight hot-orange energy flicker
        Cd.spawn(CMode.Flash, COrient.Billboard, x, y, z, 0, 0, 0, 0.05, 0, 0.13, 0.19, C.white, 1, C.fire, CFlag.Add, 4, Math.random(), 0, rot, 2 * k);
        Cd.spawn(CMode.Muzzle, COrient.Axis, x, y, z, dir.x, dir.y, dir.z, 0.05, 0, 0.55, 0.2, C.fireCore, 1, C.fire, CFlag.Add, 0, Math.random(), 0, 0, 1.8 * k);
        this.soft(fx, fy, fz, 0.55, 0.06, C.fireCore, C.fire, 0.8 * k);
        break;
      case 2: // Whisper: small, dim, violet-tinged, a wisp
        Cd.spawn(CMode.Flash, COrient.Billboard, x, y, z, 0, 0, 0, 0.05, 0, 0.1, 0.14, C.sun, 1, C.violet, CFlag.Add, 5, Math.random(), 0, rot, 1.3 * k);
        Cd.spawn(CMode.Muzzle, COrient.Axis, x, y, z, dir.x, dir.y, dir.z, 0.05, 0, 0.35, 0.14, C.sun, 1, C.violet, CFlag.Add, 0, Math.random(), 0, 0, 1.2 * k);
        this.soft(fx, fy, fz, 0.4, 0.07, C.sun, C.violet, 0.6 * k);
        this.puffs(x, y, z, 1, 0.4, 0.08, 0.28, 0.5, 0.02, 0.3, C.smokeLight, false, 0.7);
        break;
      default: // Auto MG: chunky orange star + a spent brass casing
        Cd.spawn(CMode.Flash, COrient.Billboard, x, y, z, 0, 0, 0, 0.055, 0, 0.2, 0.3, C.fireCore, 1, C.fire, CFlag.Add, 5, Math.random(), 0, rot, 2 * k);
        Cd.spawn(CMode.Muzzle, COrient.Axis, x, y, z, dir.x, dir.y, dir.z, 0.055, 0, 0.8, 0.32, C.fireCore, 1, C.fire, CFlag.Add, 0, Math.random(), 0, 0, 1.9 * k);
        this.soft(fx, fy, fz, 0.75, 0.06, C.fireCore, C.fire, 0.8 * k);
        if (Math.random() < 0.35 * this.mul) this.puffs(x + dir.x * 0.3, y + dir.y * 0.3, z + dir.z * 0.3, 1, 0.6, 0.1, 0.35, 0.6, 0.02, 0.4, C.smokeLight, false, 0.7);
        if (Math.random() < 0.6 * this.mul) {
          // casing ejects to the right of the aim
          const rx = -dir.z, rz = dir.x;
          this.particles.spawn(pos.x - dir.x * 0.3, pos.y, pos.z - dir.z * 0.3, rx * 3 + rnd(-0.5, 0.5), rnd(2, 3.5), rz * 3 + rnd(-0.5, 0.5),
            0.7, 0, 14, 0.3, this.ground(pos.x, pos.y, pos.z), PShape.Chunk, 0, 0.05, 0.05, 25, C.brass, 1, C.brass, 1);
        }
        break;
    }
  }

  tracer(from: THREE.Vector3, to: THREE.Vector3, cls: number, local: boolean) {
    const dx = to.x - from.x, dy = to.y - from.y, dz = to.z - from.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (d < 0.6) return;
    const speed = cls === 0 ? 300 : cls === 2 ? 260 : 230;
    const seg = cls === 3 ? 3.2 : cls === 0 ? 6 : 4.5;
    const w = (cls === 0 ? 0.085 : cls === 3 ? 0.075 : 0.065) * (local ? 1.15 : 1);
    const k = local ? 2.8 : 2.1;
    const head = cls === 2 ? C.violet : C.cream;
    const s = 0.5 / d; // start just past the muzzle flash
    this.cards.spawn(CMode.Streak, COrient.Segment, from.x + dx * s, from.y + dy * s, from.z + dz * s, to.x, to.y, to.z,
      Math.max(d / speed / 0.72, 0.16), 0, w, w, head, 1, C.sun, CFlag.Add, Math.min(seg / d, 1), 0, 0, 0, k);
  }

  impact(pos: THREE.Vector3, normal: THREE.Vector3, kind: number) {
    const nx = normal.x, ny = normal.y, nz = normal.z;
    // lift off the surface so the burst isn't buried in the robot / ground it hit
    const x = pos.x + nx * 0.12, y = pos.y + ny * 0.12, z = pos.z + nz * 0.12;
    const P = this.particles, Cd = this.cards;
    const m = this.ds(x, y, z) * 1.5;
    switch (kind) {
      case 1: { // world: a lit dust puff and grit
        // a round into a pond: a little crown and rings instead of dust (immersion pass)
        { const wy = this.wet(pos.x, pos.y, pos.z, 0.25); if (wy === wy) { this.water.splash(pos.x, wy, pos.z, 4.2, 0, 0); break; } }
        const fl = this.ground(x, y, z);
        for (let i = 0; i < this.n(3); i++) {
          this.dir(0.3);
          P.spawn(x, y, z, nx * 1.6 + this.dx * 0.8, ny * 1.6 + this.dy * 0.8 + 0.5, nz * 1.6 + this.dz * 0.8, rnd(0.6, 1.0), 0, -0.3, 3.5, fl,
            PShape.Puff, PFlag.Soft, 0.16 * m, rnd(0.45, 0.65) * m, rnd(-1, 1), C.tan, 1, C.dirt, 1, 1, 0.95);
        }
        for (let i = 0; i < this.n(4); i++) {
          this.dir(0.5);
          P.spawn(x, y, z, nx * 3 + this.dx * 2.5, ny * 3 + this.dy * 2 + 1.5, nz * 3 + this.dz * 2.5, rnd(0.5, 0.8), 0, 16, 0.4, fl,
            PShape.Chunk, 0, rnd(0.05, 0.09) * m, 0.05 * m, rnd(-15, 15), C.dirt, 1, C.dirt, 1, 0.8, 0.8);
        }
        this.sparks(x, y, z, this.n(6), 7, C.amber, 1.8, 0.12 * m, 0.3, 0.3, 12, -1e5, 0);
        if (y - fl < 0.6) this.scorch(x, fl, z, 0.32 * m, 4);
        // a wall keeps the mark: a sooty pock where the round bit in, there for the fight (immersion pass)
        else if (Math.abs(ny) < 0.6) this.cards.spawn(CMode.Decal, COrient.Plane, pos.x + nx * 0.025, pos.y + ny * 0.025, pos.z + nz * 0.025, nx, ny, nz,
          rnd(22, 30) * (0.6 + 0.4 * this.mul), 0, 0.16 * Math.min(m, 1.8), 0.2 * Math.min(m, 1.8), C.soot, 1, C.soot, 0, 0, 0, 0, Math.random() * 6.28, 1);
        break;
      }
      case 2: { // robot body: ink and sparks
        Cd.spawn(CMode.Flash, COrient.Billboard, x, y, z, 0, 0, 0, 0.08, 0, 0.3 * m, 0.55 * m, C.white, 1, C.amber, 0, 4, Math.random(), 0, Math.random() * 6, 1.9);
        this.soft(x, y, z, 1.4 * m, 0.12, C.fireCore, C.fire, 0.9);
        this.flare(x, y, z, 0.8 * m, 0.1, C.white, C.fire, 1.3, 1);
        // ink flecks knocked off the casing: thin inked streaks (the old round ink blobs read as
        // dark smoke RINGS at range -- distance-scaled discs with a pale core)
        this.sparks(x, y, z, this.n(4), 6, C.ink, 1, 0.07 * m, 0.3, 0.3, 10, -1e5, 0);
        for (let i = 0; i < this.n(9); i++) {
          this.dir(0.2);
          const s = rnd(4, 10);
          P.spawn(x, y, z, (nx + this.dx * 0.8) * s, (ny + this.dy * 0.8) * s + 1, (nz + this.dz * 0.8) * s, rnd(0.22, 0.38), 0, 10, 1.8, -1e5,
            PShape.Spark, PFlag.Stretch, 0.12 * m, 0.06 * m, 0, C.fireCore, 1, C.fire, 0.7, 2.2, 1.6);
        }
        // a puff of grit and paint knocked off the hit, lit like the world's smoke (~1 m at 15 m)
        for (let i = 0; i < this.n(2); i++) {
          this.dir(0.3);
          P.spawn(x, y, z, nx * 1.2 + this.dx * 0.6, ny * 1.2 + this.dy * 0.6 + 0.4, nz * 1.2 + this.dz * 0.6, rnd(0.5, 0.8), 0.02, -0.2, 3.5, -1e5,
            PShape.Puff, PFlag.Soft, 0.18 * m, rnd(0.42, 0.55) * m, rnd(-1, 1), C.smokeLight, 1, C.smokeMid, 0.9, 1, 0.95);
        }
        { const fl = this.ground(x, y, z); if (y - fl < 1.6) this.scorch(x, fl, z, 0.28 * m, 3); }
        break;
      }
      case 3: { // head: a gold pop
        Cd.spawn(CMode.Flash, COrient.Billboard, x, y, z, 0, 0, 0, 0.09, 0, 0.18 * m, 0.32 * m, C.white, 1, C.amber, 0, 8, Math.random(), 0, Math.random() * 6, 3.2);
        Cd.spawn(CMode.Shock, COrient.Billboard, x, y, z, 0, 0, 0, 0.24, 0, 0.08 * m, 0.62 * m, C.amber, 1, C.brassTrim, CFlag.Add, 0.16, 0, 0, 0, 2);
        this.flare(x, y, z, 0.9 * m, 0.14, C.white, C.amber, 1.4, 1);
        this.sparks(x, y, z, this.n(12), 9, C.amber, 2.8, 0.12 * m, 0.38, 0.4, 10, -1e5, 0);
        this.sparks(x, y, z, this.n(3), 6, C.ink, 1, 0.07 * m, 0.3, 0.4, 10, -1e5, 0);
        break;
      }
      case 4: { // armour ricochet: white sparks glancing off, a white tick
        Cd.spawn(CMode.Flash, COrient.Billboard, x, y, z, 0, 0, 0, 0.06, 0, 0.18 * m, 0.32 * m, C.white, 1, C.chrome, 0, 4, Math.random(), 0, Math.random() * 6, 1.6);
        this.flare(x, y, z, 0.6 * m, 0.08, C.white, C.chrome, 1.2, 1);
        for (let i = 0; i < this.n(7); i++) {
          this.dir(0.3);
          const s = rnd(9, 15);
          P.spawn(x, y, z, (nx + this.dx * 0.7) * s, (ny + this.dy * 0.7) * s + 1.5, (nz + this.dz * 0.7) * s, rnd(0.22, 0.4), 0, 14, 1.2, -1e5,
            PShape.Spark, PFlag.Stretch, 0.11 * m, 0.055 * m, 0, C.white, 1, C.steel, 0.6, 1.8, 1.2);
        }
        P.spawn(x, y, z, nx * 0.6, ny * 0.6 + 0.4, nz * 0.6, 0.45, 0, 0, 3, -1e5, PShape.Puff, 0, 0.12, 0.32, 0, C.smokeLight, 0.8, C.smokeLight, 0.8);
        break;
      }
      case 5: { // shield: blue hex flicker
        Cd.spawn(CMode.Hex, COrient.Billboard, x, y, z, 0, 0, 0, 0.3, 0, 0.32 * m, 0.75 * m, C.blue, 1, C.blue, CFlag.Add, 0, 0, 0, Math.random() * 6, 1.6);
        Cd.spawn(CMode.Glow, COrient.Billboard, x, y, z, 0, 0, 0, 0.08, 0, 0.14, 0.06, C.white, 1, C.blue, CFlag.Add, 0, 0, 0, 0, 1.5);
        this.soft(x, y, z, 0.8 * m, 0.15, C.white, C.blue, 0.8);
        this.sparks(x, y, z, this.n(4), 5, C.blue, 2, 0.05, 0.25, 0.2, 4);
        break;
      }
    }
  }

  // ---------------------------------------------------------------- big stuff
  explosion(pos: THREE.Vector3, radius: number, kind: number) {
    // the sim's radius is the DAMAGE radius (bomb 3.6, boulder 5, grenade 4): the ground ring shows
    // it; the fireball itself is drawn ~0.6x so a blast 3 m away doesn't fill the screen
    const R0 = Math.max(radius, 0.5), x = pos.x, y = pos.y, z = pos.z, r = R0 * 0.6;
    const fl = this.ground(x, y, z);
    const gy = Math.max(fl, y - 3);
    const P = this.particles, Cd = this.cards, D = this.debris;
    if (kind === 6) {
      // a Time grenade: no fire. A cold flash, a ring of diamonds flung out and hanging, slow motes drifting in the field
      this.soft(x, y + 0.4, z, R0 * 1.4, 0.25, C.ice, C.white, 1.0);
      for (let i = 0; i < 28; i++) {
        const a = (i / 28) * Math.PI * 2, sp = R0 * 1.6;
        P.spawn(x, y + 0.5, z, Math.cos(a) * sp, rnd(0.2, 0.9), Math.sin(a) * sp, rnd(0.9, 1.4), 0, -0.4, 2, -1e5, PShape.Diamond, PFlag.Add, 0.08, 0.04, 6, C.white, 1, C.ice, 0.7, 2.2, 1.2);
      }
      for (let i = 0; i < 18; i++) P.spawn(x + rnd(-R0, R0), y + rnd(0.2, 2.2), z + rnd(-R0, R0), rnd(-0.1, 0.1), rnd(0.05, 0.2), rnd(-0.1, 0.1), rnd(2, 3.5), 0, -0.02, 1, -1e5, PShape.Diamond, PFlag.Add, 0.05, 0.03, 2, C.ice, 0.9, C.white, 0.5, 1.4, 1.1);
      return;
    }
    const rock = kind === 3, stagger = kind === 4;
    const onGround = y - fl < r * 0.6;
    // a blast on or just over a pond throws a column of water as well (immersion pass)
    { const wy = this.wet(x, y, z, r * 0.8); if (wy === wy) this.water.splash(x, wy, z, 22 + R0 * 4, 0, 0); }
    // t=0: white-hot flash, a hard star and the soft light it throws
    Cd.spawn(CMode.Flash, COrient.Billboard, x, y + 0.3, z, 0, 0, 0, 0.14, 0, r * 0.25, r * 0.6, C.white, 1, rock ? C.hazeLight : stagger ? C.chrome : C.fire, CFlag.Add, 9, Math.random(), 0, Math.random() * 6, rock ? 1.3 : 2.2);
    this.soft(x, y + 0.4, z, r * (rock ? 1.0 : 1.2), rock ? 0.2 : 0.25, rock ? C.hazeLight : C.fireCore, rock ? C.tan : C.fire, rock ? 0.5 : 1.3);
    if (!rock) this.flare(x, y + 0.3, z, r * 1.3, 0.12, C.white, stagger ? C.chrome : C.amber, 1.6, 0);
    // damage-radius ring: a faint tan dust ring, short-lived and lifted (never a pale ribbon lying on the grass)
    Cd.spawn(CMode.Shock, COrient.Ground, x, gy + 0.06, z, 0, 0, 0, 0.32, 0, R0 * 0.15, R0, C.tan, 0.45, C.dirt, 0, 0.07, 0, 0, 0, 0.8);
    if (!rock && y - fl < r * 1.5) { this.groundGlow(x, fl, z, r * 2.2, 0.35, C.fireCore, C.fire, 1.2); this.groundGlow(x, fl, z, r * 1.5, 1.4, C.flameHot, C.flameCool, 0.5, 0.1); }
    // a second, later hot flash billboard (was a translucent shock dome) so t~0.15 still reads as fire, not glass
    Cd.spawn(CMode.Flash, COrient.Billboard, x, y + 0.4, z, 0, 0, 0, 0.3, 0.06, r * 0.35, r * 0.75, C.fireCore, 1, stagger ? C.chrome : C.fire, CFlag.Add, 6, Math.random(), 0, Math.random() * 6, rock ? 1.0 : 2.6);
    // ink-black spark streaks: printed shrapnel that reads against the bloom
    this.sparks(x, y + 0.2, z, this.n(rock ? 10 : 8), r * 3.2, C.ink, 1, 0.09, 0.55, 0.45, 14, fl, 0);
    if (stagger) {
      // stagger quake: no fire, a steel-white crack of force and a dust skirt
      this.sparks(x, y + 0.2, z, this.n(16), r * 3.5, C.white, 1.8, 0.06, 0.45, 0.4, 14, fl);
      this.dustRing(x, gy, z, this.n(10), r * 2, r * 0.18, r * 0.42, 1.0, fl);
      this.kick(0.25, DET[3].flash, 0.15, 0.4);
      return;
    }
    if (!rock) {
      // the fireball: hot puffs that roll outward and up, cooling into dark lit smoke
      const nb = this.n(12);
      for (let i = 0; i < nb; i++) {
        this.dir(0.45);
        const s = r * rnd(1.6, 2.8);
        P.spawn(x + this.dx * r * 0.12, y + 0.3 + this.dy * r * 0.1, z + this.dz * r * 0.12, this.dx * s, this.dy * s * 0.55 + r * 0.5, this.dz * s,
          rnd(1.7, 2.5), 0, -r * 0.12, 3.4, fl, PShape.Puff, PFlag.Hot, r * rnd(0.3, 0.4), r * rnd(0.6, 0.8), rnd(-0.6, 0.6),
          C.smokeDark, 1, C.smokeMid, 1, 1, 0.9);
      }
      // a column of smoke that rolls up after the fire
      this.puffs(x, y + r * 0.3, z, this.n(6), r * 0.35, r * 0.4, r * 0.95, 3.0, 0.2, r * 0.28, C.smokeDark, false, 1, 1.4);
      // licks of flame at the heart
      this.flames(x, Math.max(gy, y - 0.3), z, this.n(7), r * 0.4, r * 0.5, 0.5, 0.06, 2.5);
      this.sparks(x, y + 0.2, z, this.n(18), r * 4, C.amber, 2.4, 0.07, 0.7, 0.55, 14, fl);
      this.embers(x, y + 0.5, z, this.n(10), r * 1.2, 2.2);
      if (onGround) this.scorch(x, fl, z, r * 0.95);
    }
    if (y - fl < r * 1.5) this.dustRing(x, gy, z, this.n(rock ? 18 : 14), r * (rock ? 2.2 : 1.8), r * 0.2, r * 0.5, rock ? 1.6 : 1.2, fl);
    if (rock) this.puffs(x, gy + 0.3, z, this.n(6), r * 0.8, r * 0.25, r * 0.7, 1.6, 0.05, r * 0.25, C.tan, false, 1, 2.5);
    for (let i = 0; i < this.n(rock ? 12 : 8); i++) {
      this.dir(0.8);
      const s = rnd(0.12, 0.3) * (rock ? 1.5 : 1);
      const col = rock ? (Math.random() < 0.5 ? C.dirt : C.tan) : (Math.random() < 0.5 ? C.gunmetal : C.rust);
      D.spawn(x, y + 0.3, z, this.dx * rnd(3, 7), this.dy * rnd(5, 10) + 2, this.dz * rnd(3, 7), rnd(1.8, 2.6), 0, s, s * rnd(0.6, 1), s, fl, rnd(8, 18), col);
    }
    this.kick(rock ? 0.25 : 0.45, rock ? DET[3].flash : DET[1].flash, 0.15, rock ? 0.7 : 0.6);
  }

  detonation(pos: THREE.Vector3, radius: number, cls: number, chain: number) {
    const x = pos.x, y = pos.y, z = pos.z;
    const s = Math.max(1.5, radius) * (1 + 0.12 * Math.min(Math.max(chain, 0), 6));
    const fl = this.ground(x, y, z);
    const gy = Math.max(fl, y - 4);
    const P = this.particles, Cd = this.cards, D = this.debris;
    const f = DET[cls & 3];
    // the shared grammar: a big flash and soft light, a ground ring that runs out past the blast radius
    Cd.spawn(CMode.Flash, COrient.Billboard, x, y, z, 0, 0, 0, 0.22, 0, s * 0.2, s * 0.55, C.white, 1, f.edge, CFlag.Add, cls === 2 ? 4 : 8, Math.random(), 0, Math.random() * 6, 2.6);
    this.soft(x, y, z, s * 1.6, 0.5, f.core, f.edge, 1.3);
    Cd.spawn(CMode.Shock, COrient.Ground, x, gy, z, 0, 0, 0, 0.75, 0, s * 0.1, s * 1.15, f.core, 1, f.edge, CFlag.Add, 0.09, 0, 0, 0, 2.2);
    if (chain >= 2) Cd.spawn(CMode.Shock, COrient.Ground, x, gy, z, 0, 0, 0, 0.5, 0.12, s * 0.1, s * 0.9, f.core, 1, f.edge, CFlag.Add, 0.06, 0, 0, 0, 1.8);
    switch (cls & 3) {
      case 0: { // OVERLOAD BURST: gold ring + rays
        Cd.spawn(CMode.Rays, COrient.Billboard, x, y, z, 0, 0, 0, 0.6, 0, s * 0.3, s * 1.25, C.sun, 1, C.amber, CFlag.Add, 14, Math.random(), 0, Math.random() * 6, 2.4);
        Cd.spawn(CMode.Shock, COrient.Billboard, x, y, z, 0, 0, 0, 0.5, 0, s * 0.1, s * 1.0, C.sun, 1, C.amber, CFlag.Add, 0.1, 0, 0, 0, 2.4);
        Cd.spawn(CMode.Shock, COrient.Billboard, x, y, z, 0, 0, 0, 0.45, 0.12, s * 0.05, s * 0.7, C.white, 1, C.brassTrim, CFlag.Add, 0.06, 0, 0, 0, 2);
        this.flare(x, y, z, s * 1.4, 0.3, C.white, C.amber, 1.8, 0);
        this.sparks(x, y, z, this.n(34), 17, C.amber, 2.6, 0.16, 0.8, 0.2, 7);
        for (let i = 0; i < this.n(12); i++) {
          this.dir(0.6);
          const v = rnd(1, 3);
          P.spawn(x, y, z, this.dx * v, this.dy * v + 1, this.dz * v, rnd(0.8, 1.4), rnd(0, 0.15), -0.5, 1.5, -1e5,
            PShape.Diamond, PFlag.Add, 0.22, 0.06, rnd(-4, 4), C.sun, 1, C.amber, 0.5, 2, 1.6);
        }
        this.puffs(x, y, z, this.n(6), s * 0.7, s * 0.15, s * 0.4, 1.5, 0.1, 0.8, C.smokeLight, false, 0.9);
        break;
      }
      case 1: { // ELEMENT STORM: a swirling column of fire
        this.tornado(x, gy, z, s, 1.4);
        this.sparks(x, y, z, this.n(22), 9, C.fire, 2.2, 0.07, 0.9, 0.8, -2);
        break;
      }
      case 2: { // CHAIN FRACTURE: violet arcs and crystal shards
        Cd.spawn(CMode.Shock, COrient.Billboard, x, y, z, 0, 0, 0, 0.45, 0, s * 0.1, s * 0.95, C.violet, 1, C.violetDeep, CFlag.Add, 0.12, 0, 0, 0, 2.2);
        Cd.spawn(CMode.Diamond, COrient.Billboard, x, y, z, 0, 0, 0, 0.35, 0.03, s * 0.05, s * 0.32, C.violet, 1, C.white, CFlag.Add, 0, 0, 0, 0, 2);
        const arcs = this.n(8);
        for (let i = 0; i < arcs; i++) {
          this.dir(0.2);
          const L = s * rnd(0.55, 1.0);
          const delay = i < arcs / 2 ? rnd(0, 0.05) : rnd(0.12, 0.24);
          Cd.spawn(CMode.Arc, COrient.Segment, x, y, z, x + this.dx * L, Math.max(gy + 0.2, y + this.dy * L * 0.6), z + this.dz * L,
            rnd(0.28, 0.42), delay, s * 0.22, 0, C.violet, 1, C.violet, CFlag.Add, 0, Math.random() * 100, 0, 0, 2.2);
        }
        for (let i = 0; i < this.n(28); i++) {
          this.dir(0.3);
          const v = rnd(5, 13);
          P.spawn(x, y, z, this.dx * v, this.dy * v + 1, this.dz * v, rnd(0.6, 1.0), 0, 9, 1.4, fl,
            PShape.Shard, i & 1 ? PFlag.Add : 0, rnd(0.22, 0.42), 0.12, rnd(-14, 14), i & 1 ? C.violet : linRGB(0xe9dcff), 1, C.violetDeep, 0.7, i & 1 ? 2 : 1.1, 1);
        }
        break;
      }
      default: { // STAGGER QUAKE: cracked ground ring, rocks, dust wall
        Cd.spawn(CMode.Quake, COrient.Ground, x, gy, z, 0, 0, 0, 0.9, 0, s * 0.15, s * 1.25, C.ink, 0.95, C.ink, 0, 9, Math.random() * 50, 0, 0, 1.3);
        for (let i = 0; i < this.n(16); i++) {
          const a = Math.random() * 6.283, rr = s * rnd(0.1, 0.7);
          const sz = rnd(0.32, 0.72);
          D.spawn(x + Math.cos(a) * rr, gy + 0.2, z + Math.sin(a) * rr, Math.cos(a) * rnd(1, 4), rnd(6, 11), Math.sin(a) * rnd(1, 4), rnd(1.8, 2.6), rnd(0, 0.08),
            sz, sz * rnd(0.6, 0.9), sz * rnd(0.7, 1), fl, rnd(5, 12), Math.random() < 0.5 ? C.dirt : C.grassGold, 20, 0.3, 0.9);
        }
        const nd = this.n(18);
        for (let i = 0; i < nd; i++) {
          const a = (i / nd) * 6.283 + rnd(-0.1, 0.1), ca = Math.cos(a), sa = Math.sin(a), v = rnd(5, 9);
          P.spawn(x + ca * s * 0.25, gy + 0.3, z + sa * s * 0.25, ca * v, rnd(0.5, 2), sa * v, rnd(1.0, 1.5), rnd(0, 0.06), -0.4, 2.8, fl,
            PShape.Puff, 0, s * 0.14, s * rnd(0.32, 0.45), rnd(-0.5, 0.5), Math.random() < 0.5 ? C.tan : C.cobble, 1, C.dirt, 1, 1, 0.92);
        }
        this.sparks(x, y, z, this.n(10), 8, C.amber, 2, 0.08, 0.4, 0.6, 12);
        break;
      }
    }
    this.kick(0.85, f.flash, 0.7, cls === 3 ? 1 : 0.6);
  }

  primeBurst(pos: THREE.Vector3) {
    const x = pos.x, y = pos.y, z = pos.z;
    this.cards.spawn(CMode.Diamond, COrient.Billboard, x, y, z, 0, 0, 0, 0.38, 0, 0.12, 0.95, C.magenta, 1, C.magenta, CFlag.Add | CFlag.NoHaze, 0, 0, 0, 0, 2.2);
    this.cards.spawn(CMode.Flash, COrient.Billboard, x, y, z, 0, 0, 0, 0.1, 0, 0.18, 0.32, C.white, 1, C.magenta, CFlag.Add, 4, 0.125, 0, 0, 2);
    this.soft(x, y, z, 1.4, 0.3, C.magenta, C.magenta, 0.7);
    for (let i = 0; i < this.n(10); i++) {
      this.dir(0.3);
      const v = rnd(2.5, 5);
      this.particles.spawn(x, y, z, this.dx * v, this.dy * v + 0.5, this.dz * v, rnd(0.35, 0.55), 0, 2, 3, -1e5,
        PShape.Diamond, PFlag.Add, 0.12, 0.05, 0, C.magenta, 1, C.magenta, 0.4, 2.2, 1.6);
    }
  }

  robotDeath(pos: THREE.Vector3, type: number) {
    const def = ENEMIES[type] ?? ENEMIES[0];
    const rad = def.radius, x = pos.x, y = pos.y + def.bodyY, z = pos.z;
    const inks = INKS[type] ?? INKS[0];
    const fl = this.ground(pos.x, pos.y, pos.z);
    const D = this.debris, big = type === 5;
    // panels in the robot's own inks
    const panels = this.n(6 + rad * 6);
    for (let i = 0; i < panels; i++) {
      this.dir(0.7);
      const w = rad * rnd(0.7, 1.2);
      const px = x + this.dx * rad * 0.4, py = y + this.dy * rad * 0.3, pz = z + this.dz * rad * 0.4;
      const vx = this.dx * rnd(2.5, 6), vy = this.dy * rnd(3, 6) + rnd(3, 6), vz = this.dz * rnd(2.5, 6);
      // chunky: thick enough to have a side, never a card
      D.spawn(px, py, pz, vx, vy, vz, rnd(2.2, 3), 0, w, w * rnd(0.12, 0.2), w * rnd(0.45, 0.9), fl, rnd(9, 18), inks[i % 3]);
      if (i < this.n(6)) this.debrisTrail(px, py, pz, vx, vy, vz, 18, 0.6, fl, 4, w * 0.5);
    }
    // bolts and a spring or two
    for (let i = 0; i < this.n(6); i++) {
      this.dir(0.7);
      const b = rnd(0.06, 0.1) * (big ? 1.6 : 1);
      D.spawn(x, y, z, this.dx * rnd(3, 7), this.dy * 4 + rnd(4, 7), this.dz * rnd(3, 7), rnd(1.6, 2.4), 0, b, b * 1.6, b, fl, rnd(15, 25), Math.random() < 0.5 ? C.gunmetal : C.chrome);
    }
    if (type === 0) {
      // the toaster's last gift: two slices pop straight up
      for (let i = 0; i < 2; i++) {
        D.spawn(x + (i - 0.5) * 0.15, y + 0.2, z, rnd(-0.8, 0.8), rnd(9, 11.5), rnd(-0.8, 0.8), 3.2, 0.03 * i, 0.34, 0.06, 0.38, fl, rnd(4, 8), C.toast, 18, 0.25);
      }
    }
    // a brief HDR flash that blooms, then a smoke column that stands over the wreck
    this.cards.spawn(CMode.Flash, COrient.Billboard, x, y, z, 0, 0, 0, 0.12, 0, rad * 0.8, rad * 1.9, C.white, 1, C.fire, CFlag.Add, 7, Math.random(), 0, Math.random() * 6, 3.5);
    this.flare(x, y, z, rad * 2.4, 0.1, C.white, C.fireCore, 3, 0);
    this.puffs(x, y + rad * 0.6, z, this.n(big ? 8 : 5), rad * 0.9, rad * 0.8, rad * 2.6, 3.0, 0.25, 2.0, C.smokeLight, false, 0.75, 1.0, -1e5, 0.5, PFlag.Soft);
    this.soft(x, y, z, rad * 2.6, 0.3, C.fireCore, C.fire, 1.1);
    if (y - fl < 3) this.groundGlow(pos.x, fl, pos.z, rad * 3.2, 0.9, C.flameHot, C.flameCool, 0.7);
    this.cards.spawn(CMode.Shock, COrient.Ground, pos.x, Math.max(fl, pos.y - 2) + 0.06, pos.z, 0, 0, 0, 0.3, 0, rad * 0.4, rad * 1.8, C.tan, 0.45, C.dirt, 0, 0.08, 0, 0, 0, 0.8);
    // a small fireball that rolls into a lit smoke pall
    this.puffs(x, y, z, this.n(big ? 7 : 4), rad * 3, rad * 0.6, rad * 1.6, 1.6, 0, rad * 0.8, C.smokeMid, true, 0.85, 3.2, -1e5, 0.5, PFlag.Soft);
    this.puffs(x, y + rad * 0.3, z, this.n(big ? 6 : 3), rad * 1.6, rad * 0.7, rad * 2.2, 2.6, 0.2, 0.9, C.smokeLight, false, 0.7, 1.4, -1e5, 0.5, PFlag.Soft);
    this.flames(x, fl, z, this.n(big ? 6 : 3), rad * 0.6, rad * 0.9, 0.5, 0.04, 2);
    this.sparks(x, y, z, this.n(big ? 24 : 14), 9, C.amber, 2.2, 0.07, 0.5, 0.5, 12, fl);
    this.embers(x, y, z, this.n(big ? 10 : 5), 2, 1.8);
    if (y - fl < 2.5) this.scorch(pos.x, fl, pos.z, rad * 1.6, 5);
    this.kick(big ? 0.35 : 0.12, DET[1].flash, 0, big ? 0.6 : 0.15);
  }

  spawnWarn(pos: THREE.Vector3, type: number, seconds: number) {
    const def = ENEMIES[type] ?? ENEMIES[0];
    const R = def.radius * 1.5 + 0.5;
    const x = pos.x, y = pos.y, z = pos.z, sec = Math.max(0.2, seconds);
    this.cards.spawn(CMode.Marker, COrient.Ground, x, y + MARK_LIFT, z, 0, 0, 0, sec + 0.25, 0, R, R, C.cream, 0.95, C.ink, 0, sec, 0, 0, 0, 1.25);
    // dust geyser: a column that builds until the robot drops in
    const P = this.particles;
    const n = this.n(10 + sec * 16);
    for (let i = 0; i < n; i++) {
      const t = i / n, a = Math.random() * 6.283, rr = R * 0.5 * Math.sqrt(Math.random());
      const ca = Math.cos(a), sa = Math.sin(a), up = rnd(3, 5) + t * 6;
      P.spawn(x + ca * rr, y + 0.15, z + sa * rr, ca * 0.7, up, sa * 0.7, rnd(0.8, 1.2), t * sec, 2.5, 1.2, y,
        PShape.Puff, PFlag.Soft, 0.3 + t * 0.3, 0.8 + t * 0.9, rnd(-0.6, 0.6), Math.random() < 0.5 ? C.dirt : C.tan, 1, C.dirt, 1, 0.9, 0.85);
    }
    for (let i = 0; i < this.n(8); i++) {
      const a = Math.random() * 6.283, t = Math.random();
      P.spawn(x + Math.cos(a) * R * 0.4, y + 0.1, z + Math.sin(a) * R * 0.4, Math.cos(a) * 1.2, rnd(4, 7), Math.sin(a) * 1.2, 0.8, t * sec, 16, 0.3, y,
        PShape.Chunk, 0, 0.08, 0.06, rnd(-12, 12), C.dirt, 1, C.dirt, 1, 0.8, 0.8);
    }
    // the landing puff
    this.dustRing(x, y, z, this.n(10), 4, R * 0.35, R * 0.8, 1.0, y, sec);
    this.cards.spawn(CMode.Shock, COrient.Ground, x, y, z, 0, 0, 0, 0.45, sec, R * 0.5, R * 1.8, C.tan, 0.5, C.dirt, 0, 0.08, 0, 0, 0, 0.85);
  }

  playerHurt(pos: THREE.Vector3) {
    const x = pos.x, y = pos.y + 1.1, z = pos.z;
    this.sparks(x, y, z, this.n(6), 5, C.hurt, 2, 0.06, 0.3, 0.3, 8);
    // ink flecks as thin streaks: the old round ink blobs (on every hurt teammate) read as a row
    // of dark smoke RINGS across the field, since bots and teammates get hit constantly
    this.sparks(x, y, z, this.n(3), 4, C.ink, 1, 0.05, 0.3, 0.4, 8, -1e5, 0);
    this.screen.hurt = 1;
  }

  // ---------------------------------------------------------------- burning buildings (world/town_burn.ts)
  /** One drift of smoke off a damaged building (rate-limited by the caller). dark 0..1: grey wisps to black fire smoke. */
  buildingSmoke(x: number, y: number, z: number, size: number, dark: number) {
    const col = dark > 0.6 ? C.smokeUmber : dark > 0.25 ? C.smokeMid : C.smokeLight;
    // a slow, tall column that leans with the wind and widens as it rises
    this.particles.spawn(x + rnd(-0.4, 0.4), y, z + rnd(-0.4, 0.4), rnd(0.2, 0.7), rnd(1.6, 2.6) * (0.8 + dark * 0.6), rnd(-0.2, 0.3),
      rnd(4.5, 7) * (0.7 + dark * 0.5), 0, -0.25, 0.35, -1e5, PShape.Puff, 0, size * rnd(0.6, 0.9), size * rnd(2.4, 3.4), rnd(-0.3, 0.3),
      col as any, 0.85 + dark * 0.15, C.smokeMid as any, 0.55, 1, 0.9);
  }

  /** The fire's light pooled on the street in front of a burning building (refreshed while it burns). */
  buildingGlow(x: number, y: number, z: number, r: number) {
    this.groundGlow(x, y, z, r, 0.9, C.flameHot, C.flameCool, 0.45);
  }

  /** Dust boiling off a building's falling top: warm, thin, low, so the fold shows through it. */
  buildingDust(x: number, y: number, z: number, size: number) {
    this.particles.spawn(x, y, z, rnd(-0.8, 0.8), rnd(0.3, 1.2), rnd(-0.8, 0.8), rnd(1.8, 3), 0, -0.2, 1.2, -1e5, PShape.Puff, 0,
      size * rnd(0.4, 0.7), size * rnd(1.1, 1.6), rnd(-0.4, 0.4), C.dustOchre as any, 0.6, C.tan as any, 0, 1.15, 1);
  }

  /** One flame tongue licking out of a burning building, with the odd ember. */
  buildingFlame(x: number, y: number, z: number, size: number) {
    this.particles.spawn(x, y + size * 0.3, z, rnd(-0.25, 0.25), rnd(1.4, 2.6), rnd(-0.25, 0.25), rnd(0.55, 0.95), 0, -0.6, 1.8, -1e5,
      PShape.Fire, PFlag.Add | PFlag.Up, size * rnd(0.7, 1.0), size * rnd(1.0, 1.4), 0, C.flameHot as any, 1, C.flameCool as any, 0.6, 1.15, 1.0);
    if (Math.random() < 0.3) this.embers(x, y + size * 0.6, z, 1, 1.2, 2.4, C.ember, 2.4);
  }

  /** A building comes down: a skirt of dust that hides the swap, a rolling cloud, flying boards. */
  buildingCollapse(x: number, y0: number, z: number, hx: number, hz: number, h: number) {
    const r = Math.max(hx, hz);
    this.dustRing(x, y0, z, this.n(22), r * 1.6, r * 0.35, r * 0.9, 2.6, y0 - 0.2, 0, C.tan);
    // the cloud: rolls out of the falling walls and up past the old roofline
    for (let i = 0; i < this.n(18); i++) {
      // round the walls, not inside them: the cloud has to stand in front of the falling body
      const a = rnd(0, Math.PI * 2), px = x + Math.cos(a) * (hx + rnd(0.3, 1.5)), pz = z + Math.sin(a) * (hz + rnd(0.3, 1.5));
      this.particles.spawn(px, y0 + rnd(0.3, h * 0.5), pz, rnd(-1.5, 1.5), rnd(0.4, 1.8), rnd(-1.5, 1.5), rnd(3.5, 5.5), rnd(0, 0.12), -0.3, 1.4, y0,
        PShape.Puff, 0, r * rnd(0.45, 0.65), r * rnd(1.0, 1.4), rnd(-0.4, 0.4), (Math.random() < 0.6 ? C.dustOchre : C.tan) as any, 0.75, C.smokeLight as any, 0.5, 1.15, 1);
    }
    // a last gasp of fire and sparks out of the heap
    this.flames(x, y0 + 0.4, z, this.n(10), r * 0.6, 1.4, 0.9, 0.2, 2.2);
    this.embers(x, y0 + 1, z, this.n(24), 2.5, 2.8, C.ember, 3);
    for (let i = 0; i < this.n(14); i++) {
      this.dir(0.6);
      const s = rnd(0.14, 0.3);
      this.debris.spawn(x + rnd(-hx, hx) * 0.7, y0 + rnd(1, h * 0.7), z + rnd(-hz, hz) * 0.7, this.dx * rnd(2, 5), this.dy * rnd(2, 6) + 2, this.dz * rnd(2, 5),
        rnd(1.6, 2.4), 0, s * rnd(2.5, 5), s * 0.35, s, y0, rnd(4, 10), Math.random() < 0.6 ? C.smokeDark : C.dirt);
    }
    this.kick(0.08, null, 0.05, 0.35);
  }

  /** Splinters and a puff of plaster where a shot or a blast bit into a wall. */
  buildingChips(x: number, y: number, z: number, nx: number, nz: number) {
    for (let i = 0; i < this.n(3); i++) {
      const s = rnd(0.06, 0.14);
      this.debris.spawn(x, y, z, nx * rnd(1.5, 4) + rnd(-1, 1), rnd(1, 3.5), nz * rnd(1.5, 4) + rnd(-1, 1), rnd(0.8, 1.4), 0, s * 2.5, s * 0.4, s, y - 3, rnd(8, 16), C.dirt);
    }
  }

  /**
   * A cowboy bursts through a house wall at flight speed (sim/destruct.ts BREACH): boards and clapboard blown the
   * way he's going (into the room on the way in, out into the street on the way out), a plaster cloud that hangs
   * in the hole, splinters, and a thump on screen. (nx, nz) = the wall's outward normal, (dx, dz) = his travel.
   */
  wallBreach(x: number, y: number, z: number, nx: number, nz: number, dx: number, dz: number, exit: boolean) {
    // the ground the debris lands on: in the room, the street (or porch) outside
    const ty = this.ground(x + dx * 0.8, y, z + dz * 0.8), gs = this.ground(x - dx * 1.2, y, z - dz * 1.2);
    const tx = Math.abs(nz), tz = Math.abs(nx); // along the wall
    // long thin shards, mostly raw wood with the paint on one face: pale squares read as placeholder sprites
    // the wall's paint, a little weathered (a white-tinted panel would otherwise throw glassy white blades)
    const pw = this.wallPaint?.(x, y, z, nx, nz) ?? C.siding;
    const sat = Math.max(pw[0], pw[1], pw[2]) - Math.min(pw[0], pw[1], pw[2]);
    const paint = sat > 0.12 ? [pw[0] * 0.8, pw[1] * 0.75, pw[2] * 0.7] as const : [C.toast[0] * 0.7, C.toast[1] * 0.62, C.toast[2] * 0.55] as const; // a cream face lit white reads as glitchy quads
    const wood = () => (Math.random() < (sat > 0.12 ? 0.45 : 0.7) ? C.toast : paint); // a cream wall throws mostly bare wood
    // full-length boards: two or three tumbling end over end along his line, as long as the hole is wide
    for (let i = 0; i < 3; i++) {
      const sp = rnd(6, 10);
      this.debris.spawn(x + rnd(-0.5, 0.5) * tx, y + rnd(-0.6, 0.6), z + rnd(-0.5, 0.5) * tz, dx * sp + rnd(-1.5, 1.5), rnd(1.5, 4.5), dz * sp + rnd(-1.5, 1.5),
        rnd(2.6, 3.6), 0, 0.2, 0.035, rnd(0.9, 1.15), ty, rnd(5, 9), i === 2 ? C.toast : wood(), 16, 0.4);
    }
    // on the way in, big plank shards burst back off the face into the street, where everyone else can see them
    if (!exit) for (let i = 0; i < this.n(16); i++) {
      const sp = rnd(6, 12);
      this.debris.spawn(x + rnd(-0.6, 0.6) * tx - dx * 0.3, y + rnd(-0.7, 0.7), z + rnd(-0.6, 0.6) * tz - dz * 0.3, -dx * sp + rnd(-2.5, 2.5) * tx, rnd(2, 5), -dz * sp + rnd(-2.5, 2.5) * tz,
        rnd(2.4, 3.2), 0, rnd(0.13, 0.2), 0.05, rnd(0.5, 1.1), gs, rnd(6, 12), wood(), 16, 0.45);
    }
    // the siding: painted board pieces, most along his line, a few kicked back off the face
    // (on the way in, more of it bursts back out into the street: that's the side everyone else is watching)
    const nBack = exit ? 4 : 9;
    for (let i = 0; i < this.n(16); i++) {
      const back = i < nBack, sp = back ? rnd(3, 7) : rnd(5, 11);
      const ux = back ? -dx : dx, uz = back ? -dz : dz;
      const long = rnd(0.3, 0.6);
      this.debris.spawn(x + rnd(-0.7, 0.7) * tx, y + rnd(-0.8, 0.8), z + rnd(-0.7, 0.7) * tz,
        ux * sp + rnd(-1.8, 1.8), rnd(0.5, 4), uz * sp + rnd(-1.8, 1.8), rnd(2.2, 3.4), 0, rnd(0.1, 0.16), 0.05, long, ty, rnd(6, 14), wood(), 18, 0.5);
    }
    // splinters: a fan of thin slivers thrown along his line out of the rim
    for (let i = 0; i < this.n(26); i++) {
      const a = rnd(0, Math.PI * 2), r = rnd(0.6, 1.1), s = rnd(0.025, 0.05);
      const ox = Math.cos(a) * r * tx, oz = Math.cos(a) * r * tz, oy = Math.sin(a) * r;
      this.debris.spawn(x + ox, y + oy, z + oz, dx * rnd(4, 9) + ox * 2.5, oy * 2.5 + rnd(0.5, 2.5), dz * rnd(4, 9) + oz * 2.5, rnd(0.9, 1.6), 0,
        s, s * 0.7, s * rnd(6, 12), ty, rnd(10, 22), C.toast, 18, 0.8);
    }
    // (printed puffs, not Soft: the feathered vapour bloomed into hot blocks over the hole here)
    // plaster: a pale cone blown through along his line that hangs in the air, and a lingering cloud in the hole
    for (let i = 0; i < this.n(14); i++) {
      const t = rnd(0.1, 1), sp = rnd(3, 8) * t;
      this.particles.spawn(x + dx * t * 0.6 + rnd(-0.4, 0.4) * tx, y + rnd(-0.5, 0.5), z + dz * t * 0.6 + rnd(-0.4, 0.4) * tz,
        dx * sp + rnd(-1.6, 1.6) * tx, rnd(-0.1, 0.9), dz * sp + rnd(-1.6, 1.6) * tz, rnd(0.9, 1.4), rnd(0, 0.08), -0.15, 2.6, ty,
        PShape.Puff, 0, rnd(0.7, 1.2), rnd(3.6, 6), rnd(-0.5, 0.5), i & 1 ? C.dustOchre : C.sawdust, 0.9, C.sawdust, 0.5, 0.8, 0.75);
    }
    const side = exit ? 1 : -1;
    // the face of the wall coughs plaster back out the way he came, a wide warm cloud on the street side
    for (let i = 0; i < this.n(exit ? 4 : 14); i++) {
      const sp = rnd(3, 8);
      this.particles.spawn(x + rnd(-0.6, 0.6) * tx, y + rnd(-0.6, 0.6), z + rnd(-0.6, 0.6) * tz,
        -dx * sp + rnd(-2.6, 2.6) * tx, rnd(0, 1.2), -dz * sp + rnd(-2.6, 2.6) * tz, rnd(1.2, 2), rnd(0, 0.08), -0.1, 2.2, ty,
        PShape.Puff, 0, rnd(0.8, 1.3), rnd(4.6, 7.2), rnd(-0.5, 0.5), i & 1 ? C.dustOchre : C.sawdust, 0.9, C.sawdust, 0.5, 0.8, 0.75);
    }
    this.puffs(x - nx * 0.3 * side, y, z - nz * 0.3 * side, this.n(6), 0.7, 0.6, 2.4, 1.5, 0.05, 0.12, C.smokeLight, false, 0.28, 2, ty, 0.2, PFlag.Soft);
    this.dustRing(x + dx * 1.5, ty, z + dz * 1.5, this.n(12), 3.2, 0.45, 1.5, 1.5, ty - 0.2, 0.25, C.dustOchre);
    if (!exit) this.dustRing(x - dx * 1.2, gs, z - dz * 1.2, this.n(10), 2.8, 0.4, 1.3, 1.3, gs - 0.2, 0.25, C.dustOchre);
    this.kick(0, null, 0.03, 0.25);
  }

  /** A cowboy flew face-first into rock (a bonk): grit and chips off the face he hit, a puff of stone dust. */
  rockBonk(x: number, y: number, z: number, nx: number, nz: number, size: number) {
    for (let i = 0; i < this.n(6 + size * 6); i++) {
      const s = rnd(0.05, 0.13) * (0.8 + size * 0.4);
      this.debris.spawn(x, y, z, nx * rnd(1.5, 4) + rnd(-1.5, 1.5), rnd(1, 3.5), nz * rnd(1.5, 4) + rnd(-1.5, 1.5), rnd(0.9, 1.5), 0, s * 1.6, s * 0.7, s, y - 3, rnd(8, 18), Math.random() < 0.5 ? C.tan : C.cobble);
    }
    this.puffs(x, y, z, this.n(3 + size * 3), 1.4, 0.25, 0.9 + size * 0.5, 1.3, 0, 0.2, C.dustOchre, false, 0.75, 2.5, -1e5, 0.3, PFlag.Soft);
  }

  /**
   * A tossed whiskey empty smashes (heal lane, client/render/cowboys/whiskey.ts): amber glass skitters out low and
   * lies glinting, the dregs splash and leave a dark wet stain. `vx vy vz` is how it came in, so the glass carries on.
   */
  glassShatter(x: number, y: number, z: number, vx: number, vy: number, vz: number) {
    const hx = vx * 0.25, hz = vz * 0.25;
    // a spike (driven straight down) bursts wider than a lob, with a kick of dust
    const hard = Math.max(0, Math.min(1, (-vy - 9.5) / 4)), hk = 1 + 0.6 * hard;
    if (hard > 0) this.dustRing(x, y, z, this.n(6), 1.6, 0.3, 0.9, 1.4, y - 0.2, 0, C.dustOchre);
    // the glass: thin shards, mostly skidding along the ground, a few kicked higher. Half of them are the pale lit
    // edge: amber alone vanishes on desert sand
    for (let i = 0; i < this.n(20 + 12 * hard); i++) {
      const a = Math.random() * Math.PI * 2, sp = rnd(1.2, 3.6) * hk, up = i < 5 ? rnd(2.4, 3.8) : rnd(0.6, 1.8);
      const s = rnd(0.035, 0.08);
      this.debris.spawn(x, y + 0.05, z, Math.cos(a) * sp + hx, up, Math.sin(a) * sp + hz, rnd(2.6, 4.2), 0,
        s * rnd(0.9, 1.6), s * 0.18, s, y, rnd(14, 26), i % 2 === 0 ? C.glassHi : C.glass, 16, 1.4);
    }
    // the crack itself: one hard white star on the ground for a couple of frames
    this.particles.spawn(x, y + 0.1, z, 0, 0, 0, 0.09, 0, 0, 1, y - 1, PShape.Shard, PFlag.Add, 0.45 * hk, 0.1, rnd(-3, 3), C.white, 1, C.glassHi, 0, 2.5, 1);
    // the bottle's base: one chunky piece that rolls a little way
    this.debris.spawn(x, y + 0.04, z, hx * 1.5 + rnd(-0.4, 0.4), 0.9, hz * 1.5 + rnd(-0.4, 0.4), 4.5, 0, 0.075, 0.03, 0.075, y, rnd(3, 6), C.glassDeep, 16, 1.8);
    // glints: the sun catching edges as the glass flies
    for (let i = 0; i < this.n(14); i++) {
      this.dir(0.6);
      const sp = rnd(1.5, 3.4);
      this.particles.spawn(x, y + 0.08, z, this.dx * sp + hx, this.dy * sp, this.dz * sp + hz, rnd(0.3, 0.65), 0, 9, 1, y,
        PShape.Shard, PFlag.Add, rnd(0.08, 0.14), 0.03, rnd(-16, 16), C.white, 1, C.glass, 0.4, 2, 1);
    }
    // the dregs: an amber splash crown and droplets
    for (let i = 0; i < this.n(16); i++) {
      const a = Math.random() * Math.PI * 2, sp = rnd(0.6, 2.2);
      this.particles.spawn(x, y + 0.06, z, Math.cos(a) * sp + hx * 0.5, rnd(1.2, 3), Math.sin(a) * sp + hz * 0.5, rnd(0.35, 0.65), 0, 12, 1.2, y - 0.02,
        PShape.Blob, 0, rnd(0.035, 0.06), 0.02, 0, C.whiskey, 1, C.whiskeyDark, 0.9, 1.15, 1);
    }
    this.cards.spawn(CMode.Shock, COrient.Ground, x, y + 0.04, z, 0, 0, 0, 0.32, 0, 0.08, 0.7, C.whiskey, 0.55, C.whiskeyDark, 0, 0.08, 0, 0, 0, 1);
    // a wet stain that soaks in over a few seconds
    this.cards.spawn(CMode.Decal, COrient.Ground, x + hx * 0.15, y + 0.02, z + hz * 0.15, 0, 0, 0, 7 * (0.6 + 0.4 * this.mul), 0, 0.32, 0.5, C.whiskeyStain, 0.7, C.whiskeyDark, 0, 0, 0, 0, Math.random() * 6.28, 1);
  }

  /** A stone hits the ground: grit and a low dust puff. */
  stoneThud(x: number, y: number, z: number, size: number) {
    { const wy = this.wet(x, y, z, 0.8); if (wy === wy) { this.water.splash(x, wy, z, 14 + size * 14, 0, 0); return; } }
    this.dustRing(x, y, z, this.n(6 + size * 6), 1.6 + size * 1.4, 0.4 + size * 0.3, 1.2 + size * 0.8, 2.2, y - 0.2, 0, C.dustOchre);
    // grit and stone chips knocked off the edge that hit
    for (let i = 0; i < this.n(3 + size * 4); i++) {
      const s = rnd(0.06, 0.16) * (0.7 + size * 0.4);
      this.debris.spawn(x + rnd(-0.5, 0.5), y + 0.2, z + rnd(-0.5, 0.5), rnd(-3, 3), rnd(2, 4.5), rnd(-3, 3), rnd(1, 1.8), 0, s * 2.2, s * 0.6, s, y - 1, rnd(6, 14), C.tan);
    }
  }

  dust(pos: THREE.Vector3, amount: number) {
    const a = Math.max(0, Math.min(1.5, amount));
    this.dustRing(pos.x, pos.y, pos.z, this.n(3 + a * 7), 1.5 + a * 2.5, 0.25, 0.6 + a * 0.6, 0.9, pos.y - 0.2);
  }

  jetFlame(pos: THREE.Vector3, dir: THREE.Vector3, power: number) {
    if (power <= 0.02) return;
    const p = Math.min(power, 1.5);
    this.cards.spawn(CMode.Flame, COrient.Axis, pos.x, pos.y, pos.z, dir.x, dir.y, dir.z, 0.05, 0, 0.3 + 0.5 * p, 0.14 + 0.08 * p,
      C.fireCore, 1, C.fire, CFlag.Add, 0, Math.random() * 10, 0, 0, 2.1);
    if (Math.random() < 0.5) this.soft(pos.x + dir.x * 0.2, pos.y + dir.y * 0.2, pos.z + dir.z * 0.2, 0.45 + 0.3 * p, 0.06, C.fireCore, C.fire, 0.5);
    if (Math.random() < (0.3 + 0.4 * p) * this.mul) {
      const s = 4 + 5 * p;
      this.particles.spawn(pos.x + dir.x * 0.3, pos.y + dir.y * 0.3, pos.z + dir.z * 0.3, dir.x * s + rnd(-0.6, 0.6), dir.y * s + rnd(-0.3, 0.3), dir.z * s + rnd(-0.6, 0.6),
        rnd(0.18, 0.3), 0, -1, 5, -1e5, PShape.Blob, PFlag.Add, 0.1, 0.26, rnd(-2, 2), C.fire, 1, C.red, 0, 1.6, 0.8);
    }
    if (Math.random() < 0.12 * p * this.mul) {
      const s = 3 + 3 * p;
      this.particles.spawn(pos.x + dir.x * 0.6, pos.y + dir.y * 0.6, pos.z + dir.z * 0.6, dir.x * s, dir.y * s, dir.z * s, rnd(0.7, 1.1), 0, -0.8, 3, -1e5,
        PShape.Puff, 0, 0.2, 0.6, rnd(-1, 1), C.smokeLight, 0.85, C.smokeLight, 0.85);
    }
  }

  damageNumber(pos: THREE.Vector3, dmg: number, flags: number, mine: boolean) {
    let fill: readonly number[] = C.cream, ink: readonly number[] = C.ink, scale = 1, life = 0.9;
    if (flags & 8) { fill = C.amber; ink = linMag; scale = 1.7; life = 1.3; }
    else if (flags & 1) { fill = goldBright; scale = 1.25; }
    else if (flags & 2) { fill = C.blue; ink = navy; }
    else if (flags & 4) { fill = greyArmor; scale = 0.9; }
    else if (flags & 16) { fill = C.fire; }
    if (mine) scale *= 1.3; else { scale *= 0.85; }
    // merge per target and style; others' plain hits are dropped once ~10 numbers are up
    // one rising total per target (style upgrades to the strongest hit), laid out so numbers never
    // overlap; others' plain hits are dropped once ~8 numbers are up
    const prio = flags & 8 ? 3 : flags & 1 ? 2 : flags & 2 ? 1 : 0;
    this.numbers.spawn(pos.x, pos.y, pos.z, dmg, scale, life, fill as any, ink as any, prio, mine || (flags & 9) !== 0, 8, mine);
  }

  bullets(list: readonly { id: number; kind: number; x: number; y: number; z: number; vx: number; vy: number; vz: number; age: number }[], count: number) {
    this.bulletView.sync(list, count);
    // a red pop where each new pellet/slug leaves the muzzle; smoke and sparks behind lobbed ordnance
    const trail = this.time - this.lastTrail > 1 / 30;
    if (trail) this.lastTrail = this.time;
    const seen = this.seenIds;
    const P = this.particles;
    this.nSeen.fill(0);
    for (let i = 0; i < count; i++) {
      const b = list[i];
      const h = b.id & 4095;
      if (b.kind >= 4 && b.kind <= 6) this.trackNade(b.id, b.kind, b.x, b.y, b.z);
      if (seen[h] !== b.id) {
        seen[h] = b.id;
        if (b.age < 0.1 && b.kind <= 1) {
          this.cards.spawn(CMode.Flash, COrient.Billboard, b.x, b.y, b.z, 0, 0, 0, 0.07, 0, 0.12, 0.26, C.white, 1, C.laser, CFlag.Add, 5, Math.random(), 0, Math.random() * 6, 1.6);
          this.soft(b.x, b.y, b.z, b.kind === 1 ? 1.2 : 0.8, 0.12, C.laser, C.laser, 0.9);
        }
      }
      if (!trail || b.kind < 2) continue;
      if (b.kind === 4) {
        // ice grenade: a frost-mist wake and glints
        P.spawn(b.x, b.y, b.z, rnd(-0.3, 0.3), rnd(-0.1, 0.3), rnd(-0.3, 0.3), rnd(0.5, 0.8), 0, -0.1, 2, -1e5, PShape.Puff, 0, 0.14, 0.4, rnd(-1, 1), C.ice, 0.8, C.iceDeep, 0.6, 1.1, 1);
        if (Math.random() < 0.5) P.spawn(b.x, b.y, b.z, rnd(-1, 1), rnd(-1, 1), rnd(-1, 1), 0.3, 0, 2, 2, -1e5, PShape.Diamond, PFlag.Add, 0.06, 0.03, 4, C.white, 1, C.ice, 0.6, 2.4, 1.6);
      } else if (b.kind === 5) {
        // magnetic grenade: violet-cyan crackle that orbits it
        if (Math.random() < 0.6) this.cards.spawn(CMode.Arc, COrient.Segment, b.x, b.y, b.z, b.x + rnd(-0.6, 0.6), b.y + rnd(-0.6, 0.6), b.z + rnd(-0.6, 0.6), 0.06, 0, 0.25, 0, C.shock, 1, C.violet, CFlag.Add, 0, Math.random() * 100, 0, 0, 2.6);
      } else if (b.kind === 6) {
        // gas canister: a hissing mustard jet
        P.spawn(b.x, b.y + 0.1, b.z, -b.vx * 0.15 + rnd(-0.3, 0.3), rnd(0.2, 0.6), -b.vz * 0.15 + rnd(-0.3, 0.3), rnd(0.7, 1.0), 0, -0.2, 2, -1e5, PShape.Puff, 0, 0.12, 0.45, rnd(-1, 1), C.gas, 0.85, C.gasDeep, 0.7);
      } else if (b.kind === 2) {
        // fuse sparks + a thin grey smoke thread
        P.spawn(b.x, b.y + 0.42, b.z, rnd(-1.5, 1.5), rnd(0.5, 2.5), rnd(-1.5, 1.5), rnd(0.15, 0.3), 0, 6, 1, -1e5,
          PShape.Spark, PFlag.Add | PFlag.Stretch, 0.03, 0.02, 0, C.fireCore, 1, C.fire, 0.5, 2, 1.5);
        if (Math.random() < 0.5 * this.mul) P.spawn(b.x, b.y + 0.3, b.z, -b.vx * 0.1, 0.5, -b.vz * 0.1, rnd(0.6, 0.9), 0, -0.5, 2, -1e5,
          PShape.Puff, 0, 0.12, 0.35, rnd(-1, 1), C.smokeMid, 0.85, C.smokeMid, 0.85);
      } else if (Math.random() < 0.7 * this.mul) {
        // boulder: dust and grit shed as it tumbles
        P.spawn(b.x, b.y, b.z, rnd(-0.5, 0.5), rnd(-0.2, 0.4), rnd(-0.5, 0.5), rnd(0.6, 1.0), 0, -0.2, 2, -1e5,
          PShape.Puff, 0, 0.25, 0.6, rnd(-1, 1), C.tan, 0.9, C.tan, 0.9);
      }
    }
    this.landNades();
  }

  private trackNade(id: number, kind: number, x: number, y: number, z: number) {
    let s = -1, free = -1;
    for (let i = 0; i < 32; i++) { if (this.nId[i] === id) { s = i; break; } if (free < 0 && this.nId[i] < 0) free = i; }
    if (s < 0) { if (free < 0) return; s = free; this.nId[s] = id; this.nKind[s] = kind; }
    this.nX[s] = x; this.nY[s] = y; this.nZ[s] = z; this.nSeen[s] = 1;
  }
  /** grenades that left the list this frame burst where they last were */
  private landNades() {
    for (let i = 0; i < 32; i++) {
      if (this.nId[i] < 0 || this.nSeen[i]) continue;
      const x = this.nX[i], y = this.nY[i], z = this.nZ[i], k = this.nKind[i];
      this.nId[i] = -1;
      if (k === 4) {
        // ice: a frost nova, crystals that stand up, cold mist
        this.cards.spawn(CMode.Shock, COrient.Ground, x, this.ground(x, y, z) + 0.1, z, 0, 0, 0, 0.5, 0, 0.4, 4, C.white, 0.95, C.ice, 0, 0.12, 0, 0, 0, 1.6);
        this.soft(x, y + 0.3, z, 3, 0.3, C.white, C.ice, 2.4);
        this.element(this.tmp.set(x, y, z), El.Ice, 1.6);
        this.puffs(x, y + 0.3, z, this.n(6), 3, 0.6, 1.4, 1.3, 0, 0.3, C.ice, false, 0.8, 2.5);
      } else if (k === 5) {
        // magnetic: a violet ring snap and arcs that lash out
        this.flare(x, y + 0.3, z, 2.4, 0.12, C.white, C.violet, 3, 0);
        this.cards.spawn(CMode.Shock, COrient.Billboard, x, y + 0.3, z, 0, 0, 0, 0.3, 0, 0.3, 3.6, C.shock, 1, C.violet, CFlag.Add, 0.06, 0, 0, 0, 2.4);
        for (let j = 0; j < this.n(6); j++) {
          const a = Math.random() * 6.283, r = rnd(1.5, 3.5);
          this.cards.spawn(CMode.Arc, COrient.Segment, x, y + 0.3, z, x + Math.cos(a) * r, y + rnd(-0.3, 1.5), z + Math.sin(a) * r, rnd(0.12, 0.22), 0, 0.5, 0, C.shock, 1, C.violet, CFlag.Add, 0, Math.random() * 100, 0, 0, 2.8);
        }
      } else {
        // gas canister pops: a burst of mustard smoke (the lingering cloud comes from fx.hazard)
        this.puffs(x, y + 0.2, z, this.n(8), 3, 0.5, 1.4, 1.6, 0, 0.4, C.gas, false, 0.85, 2.5);
      }
    }
  }

  bombMarker(id: number, x: number, y: number, z: number, radius: number, seconds: number) {
    const slot = this.markerSlot.get(id);
    y += MARK_LIFT;
    if (slot !== undefined && this.cards.t0(slot) === this.markerT0.get(id)) { this.cards.move(slot, x, y, z); return; }
    const s = this.cards.spawn(CMode.Marker, COrient.Ground, x, y, z, 0, 0, 0, Math.max(0.1, seconds) + 0.22, 0, radius, radius,
      C.laser, 0.95, C.hurt, 0, Math.max(0.1, seconds), 0, 0, 0, 1.4);
    this.markerSlot.set(id, s);
    this.markerT0.set(id, this.cards.t0(s));
  }

  clearBombMarker(id: number) {
    const slot = this.markerSlot.get(id);
    if (slot === undefined) return;
    const t0 = this.markerT0.get(id)!;
    if (this.cards.t0(slot) === t0) this.cards.endAt(slot, Math.max(0, this.time - t0));
    this.markerSlot.delete(id);
    this.markerT0.delete(id);
  }

  // ---------------------------------------------------------------- elements (not in FxApi yet)
  /** A one-shot elemental proc where a hit landed: el = El (0 fire 1 acid 2 shock 3 ice 4 water 5 steam). */
  element(pos: THREE.Vector3, el: number, strength = 1) {
    const x = pos.x, y = pos.y, z = pos.z, k = Math.max(0.3, Math.min(2, strength));
    const P = this.particles;
    switch (el) {
      case El.Fire:
        this.flames(x, y - 0.2, z, this.n(4 * k), 0.25 * k, 0.35 * k, 0.45, 0.05, 1.8);
        this.embers(x, y, z, this.n(6 * k), 1.5, 1.0);
        this.soft(x, y, z, 1.0 * k, 0.25, C.fireCore, C.fire, 0.7);
        break;
      case El.Acid:
        for (let i = 0; i < this.n(8 * k); i++) {
          this.dir(0.5);
          const v = rnd(1.5, 3.5);
          P.spawn(x, y, z, this.dx * v, this.dy * v + 1, this.dz * v, rnd(0.4, 0.7), 0, 12, 1, -1e5,
            PShape.Drop, PFlag.Stretch, rnd(0.06, 0.1), 0.05, 0, C.acid, 1, C.acidDeep, 1, 1.1, 1);
        }
        this.puffs(x, y, z, this.n(2 * k), 0.5, 0.15, 0.45, 0.9, 0, 0.6, C.acidFume, false, 0.75);
        this.soft(x, y, z, 0.9 * k, 0.2, C.acid, C.acidDeep, 0.6);
        break;
      case El.Shock:
        for (let i = 0; i < this.n(4 * k); i++) {
          this.dir(0.1);
          const L = rnd(0.5, 1.2) * k;
          this.cards.spawn(CMode.Arc, COrient.Segment, x, y, z, x + this.dx * L, y + this.dy * L, z + this.dz * L, rnd(0.12, 0.2), rnd(0, 0.06), 0.25 * k, 0,
            C.shock, 1, C.shockDeep, CFlag.Add, 0, Math.random() * 100, 0, 0, 2.2);
        }
        this.flare(x, y, z, 0.9 * k, 0.1, C.white, C.shock, 1.6, 1);
        this.sparks(x, y, z, this.n(6 * k), 6, C.shock, 2, 0.04, 0.25, 0.2, 6);
        break;
      case El.Ice:
        for (let i = 0; i < this.n(7 * k); i++) {
          this.dir(0.4);
          const v = rnd(2, 5);
          P.spawn(x, y, z, this.dx * v, this.dy * v + 1, this.dz * v, rnd(0.5, 0.8), 0, 10, 1.5, this.ground(x, y, z),
            PShape.Crystal, 0, rnd(0.1, 0.2), 0.08, rnd(-10, 10), C.ice, 1, C.iceDeep, 1, 1.2, 1);
        }
        this.puffs(x, y, z, this.n(3 * k), 0.6, 0.18, 0.5, 1.0, 0, 0.1, C.ice, false, 0.6, 2.5, -1e5, 0);
        this.flare(x, y, z, 0.7 * k, 0.12, C.white, C.ice, 1.2, 2);
        break;
      case El.Water:
        for (let i = 0; i < this.n(10 * k); i++) {
          this.dir(0.6);
          const v = rnd(2, 4.5);
          P.spawn(x, y, z, this.dx * v, this.dy * v + 1.5, this.dz * v, rnd(0.4, 0.7), 0, 14, 0.6, -1e5,
            PShape.Drop, PFlag.Stretch, rnd(0.05, 0.09), 0.04, 0, C.water, 1, C.waterDeep, 1, 1.15, 1);
        }
        break;
      default: // steam
        this.puffs(x, y, z, this.n(4 * k), 0.8, 0.25, 0.8, 1.4, 0, 1.2, C.steam, false, 0.9, 1.5);
        break;
    }
  }

  /** A lingering hazard: ground footprint + continuous emission until `seconds` pass or clearHazard(id). */
  hazard(id: number, el: number, x: number, y: number, z: number, radius: number, seconds: number) {
    let s = -1;
    for (let i = 0; i < MAX_HAZ; i++) if (this.hId[i] === id) { s = i; break; }
    if (s < 0) {
      let best = 0, bestEnd = Infinity;
      for (let i = 0; i < MAX_HAZ; i++) {
        if (this.hId[i] < 0 || this.hEnd[i] <= this.time) { s = i; break; }
        if (this.hEnd[i] < bestEnd) { bestEnd = this.hEnd[i]; best = i; }
      }
      if (s < 0) s = best;
    }
    this.hId[s] = id; this.hEl[s] = el; this.hX[s] = x; this.hY[s] = y; this.hZ[s] = z; this.hR[s] = radius;
    this.hEnd[s] = this.time + seconds; this.hAcc[s] = 0; this.hAcc2[s] = 0;
    // the footprint decal
    let style = -1; let c1: readonly number[] = C.soot, c2: readonly number[] = C.ember;
    switch (el) {
      case El.Fire: style = 0; break;
      case El.Acid: style = 1; c1 = C.acid; c2 = C.acidDeep; break;
      case El.Gas: style = -1; break;
      case El.Shock: style = 2; c1 = C.shock; c2 = C.soot; break;
      case El.Ice: style = 3; c1 = C.ice; c2 = C.iceDeep; break;
      case El.Water: case El.Steam: style = 4; c1 = C.water; c2 = C.waterDeep; break;
      case El.Tornado: style = 0; break;
    }
    if (style >= 0) {
      const slot = this.cards.spawn(CMode.Decal, COrient.Ground, x, y + DECAL_LIFT, z, 0, 0, 0, seconds + 0.6, 0, radius * 0.6, radius * (style === 4 ? 1.1 : 1.15),
        c1 as any, el === El.Steam ? 0.6 : 0.95, c2 as any, 0, style, Math.random() * 50, 0, Math.random() * 6.28, 1);
      this.hDecal[s] = slot; this.hDecalT0[s] = this.cards.t0(slot);
    } else this.hDecal[s] = -1;
    // set-pieces that are placed once
    if (el === El.Ice) {
      const n = this.n(14 * Math.min(2, radius / 2));
      for (let i = 0; i < n; i++) {
        const a = Math.random() * 6.283, rr = radius * 0.85 * Math.sqrt(Math.random());
        const sz = rnd(0.6, 1.4) * (1 - 0.4 * rr / radius);
        this.particles.spawn(x + Math.cos(a) * rr, y + sz * 0.6, z + Math.sin(a) * rr, 0, 0, 0, seconds, rnd(0, 0.15), 0, 1, -1e5,
          PShape.Crystal, PFlag.Up, sz * 0.3, sz, 0, C.ice, 1, C.iceDeep, 0.9, 1.25, 1.1);
      }
      this.puffs(x, y + 0.2, z, this.n(6), radius * 0.8, radius * 0.3, radius * 0.6, 1.4, 0, 0.2, C.ice, false, 0.7, 2.5, -1e5, 0);
    } else if (el === El.Fire) {
      this.flames(x, y, z, this.n(8 * radius / 2), radius * 0.7, 0.6, 0.5, 0.1, 2);
    } else if (el === El.Tornado) {
      this.tornado(x, y, z, radius, Math.min(seconds, 1.5));
    }
  }

  clearHazard(id: number) {
    for (let i = 0; i < MAX_HAZ; i++) {
      if (this.hId[i] !== id) continue;
      this.hId[i] = -1;
      const slot = this.hDecal[i];
      if (slot >= 0 && this.cards.t0(slot) === this.hDecalT0[i]) this.cards.endAt(slot, Math.max(0, this.time - this.hDecalT0[i]) + 0.5);
    }
  }

  /** Element reactions: kind El.Tornado fire tornado, El.Steam steam cloud, El.Shock chain-shock burst. */
  reaction(pos: THREE.Vector3, kind: number, radius: number, seconds = 2) {
    const x = pos.x, y = pos.y, z = pos.z;
    if (kind === El.Tornado) { this.tornado(x, this.ground(x, y, z), z, radius, seconds); this.kick(0.3, DET[1].flash, 0.1, 0.3); }
    else if (kind === El.Steam) {
      const g = this.ground(x, y, z);
      this.puffs(x, g + 0.5, z, this.n(14), radius * 1.4, radius * 0.4, radius * 0.95, seconds, 0, 0.9, C.steam, false, 1, 1.6, g, 0.3);
      this.puffs(x, g + 1.0, z, this.n(8), radius * 0.5, radius * 0.45, radius * 1.1, seconds * 1.2, 0.3, 1.3, C.steam, false, 1, 1.1, g, 0.6);
      this.cards.spawn(CMode.Shock, COrient.Ground, x, g, z, 0, 0, 0, 0.5, 0, radius * 0.2, radius * 1.2, C.steam, 0.8, C.water, 0, 0.1, 0, 0, 0, 1);
      this.soft(x, g + 0.6, z, radius * 1.4, 0.35, C.white, C.water, 0.5);
    } else if (kind === El.Shock) {
      const n = this.n(10);
      for (let i = 0; i < n; i++) {
        this.dir(0.2);
        const L = radius * rnd(0.5, 1.0);
        this.cards.spawn(CMode.Arc, COrient.Segment, x, y, z, x + this.dx * L, Math.max(this.ground(x, y, z) + 0.1, y + this.dy * L * 0.5), z + this.dz * L,
          rnd(0.2, 0.35), rnd(0, 0.15), radius * 0.18, 0, C.shock, 1, C.shockDeep, CFlag.Add, 0, Math.random() * 100, 0, 0, 2.4);
      }
      this.flare(x, y, z, radius * 0.9, 0.15, C.white, C.shock, 1.8, 2);
      this.soft(x, y, z, radius * 1.4, 0.3, C.shock, C.shockDeep, 0.9);
      this.sparks(x, y, z, this.n(16), 9, C.shock, 2.2, 0.05, 0.35, 0.3, 8);
      this.kick(0.25, SHOCK_FLASH, 0.2, 0.2);
    }
  }

  /** A jagged arc between two points (chain shock hop, magneto zap). */
  chainArc(from: THREE.Vector3, to: THREE.Vector3, el = El.Shock) {
    const col = el === El.Shock ? C.shock : C.violet, deep = el === El.Shock ? C.shockDeep : C.violetDeep;
    const d = Math.hypot(to.x - from.x, to.y - from.y, to.z - from.z);
    this.cards.spawn(CMode.Arc, COrient.Segment, from.x, from.y, from.z, to.x, to.y, to.z, 0.3, 0, Math.min(1.2, 0.25 + d * 0.08), 0,
      col, 1, deep, CFlag.Add, 0, Math.random() * 100, 0, 0, 2.6);
    this.flare(to.x, to.y, to.z, 0.9, 0.12, C.white, col, 1.6, 1);
    this.sparks(to.x, to.y, to.z, this.n(5), 6, col, 2, 0.04, 0.25, 0.2, 6);
  }

  /**
   * Disruptor heal/shield aura. Call every frame while it is up (it fades ~0.3 s after the last
   * call). id = the enemy id, (x, y, z) = its feet, radius = the aura radius (6 m in the sim).
   */
  supportAura(id: number, x: number, y: number, z: number, radius: number) {
    let s = -1, free = -1;
    for (let i = 0; i < MAX_AURA; i++) {
      if (this.aId[i] === id) { s = i; break; }
      if (free < 0 && this.time - this.aLast[i] > 0.4) free = i;
    }
    if (s < 0) { if (free < 0) return; s = free; this.aId[s] = id; this.aAcc[s] = 0; this.aPulse[s] = 0; }
    this.aLast[s] = this.time;
    const dt = Math.min(0.1, Math.max(0, this.time - this.aPrev[s])); this.aPrev[s] = this.time;
    const g = this.ground(x, y + 1, z);
    // a steady rim at the radius (overlapping short cards follow the robot) and a pulse that rolls out
    this.aAcc[s] += dt;
    if (this.aAcc[s] > 0.12) {
      this.aAcc[s] = 0;
      this.cards.spawn(CMode.Shock, COrient.Ground, x, g + MARK_LIFT, z, 0, 0, 0, 0.18, 0, radius, radius, C.aura, 1, C.blue, CFlag.Add, 0.07, 0, 0, 0, 2.6);
      // shield hexes drift up inside the field
      const a = Math.random() * 6.283, rr = radius * 0.9 * Math.sqrt(Math.random());
      this.particles.spawn(x + Math.cos(a) * rr, g + 0.2, z + Math.sin(a) * rr, 0, rnd(0.8, 1.4), 0, rnd(0.8, 1.2), 0, -0.2, 1, -1e5,
        PShape.Hex, PFlag.Add, 0.22, 0.36, rnd(-1, 1), C.aura, 1, C.blue, 0.5, 2.6, 1.8);
    }
    this.aPulse[s] += dt;
    if (this.aPulse[s] > 1.1) {
      this.aPulse[s] = 0;
      this.cards.spawn(CMode.Shock, COrient.Ground, x, g + MARK_LIFT, z, 0, 0, 0, 0.9, 0, radius * 0.15, radius, C.aura, 0.7, C.blue, CFlag.Add, 0.08, 0, 0, 0, 1.4);
      this.groundGlow(x, g, z, radius * 0.5, 0.8, C.aura, C.blue, 0.5);
    }
  }

  /**
   * Engineer repair beam from the medic's emitter to its patient. Call every frame while it
   * channels; nothing persists, so stopping the calls stops the beam.
   */
  repairBeam(fx_: number, fy: number, fz: number, tx: number, ty: number, tz: number) {
    if (this.time - this.beamLast < 1 / 40) return;
    this.beamLast = this.time;
    // a welding-arc tether: a steady cyan-white core with a crackling sheath, sparks at the patch
    this.cards.spawn(CMode.Arc, COrient.Segment, fx_, fy, fz, tx, ty, tz, 0.06, 0, 0.35, 0, C.white, 1, C.aura, CFlag.Add, 0, Math.random() * 100, 0, 0, 2.8);
    this.cards.spawn(CMode.Streak, COrient.Segment, fx_, fy, fz, tx, ty, tz, 0.06, 0, 0.05, 0.05, C.white, 1, C.aura, CFlag.Add, 1, 0, 0, 0, 2.4);
    this.soft(tx, ty, tz, 0.9, 0.08, C.white, C.aura, 1.6);
    if (Math.random() < 0.6) this.sparks(tx, ty, tz, 2, 4, C.aura, 2.6, 0.08, 0.3, 0.5, 10);
  }

  /** Warp gate charge, call every frame (charge 0 hides it). Ring centre, radius and its facing normal (xz). */
  gateCharge(x: number, y: number, z: number, radius: number, nx: number, nz: number, charge: number) {
    this.gX = x; this.gY = y; this.gZ = z; this.gR = radius;
    const l = Math.hypot(nx, nz) || 1; this.gNx = nx / l; this.gNz = nz / l;
    this.gCharge = Math.max(0, Math.min(1, charge)); this.gOn = charge > 0.01;
  }

  private tornado(x: number, gy: number, z: number, s: number, dur: number) {
    const P = this.particles;
    const n = this.n(80 * Math.min(2, dur));
    for (let i = 0; i < n; i++) {
      const r0 = s * rnd(0.12, 0.4);
      P.spawn(x, gy + rnd(0, 0.8), z, r0, rnd(3, 7), rnd(4.5, 7.5), rnd(0.9, 1.4), rnd(0, dur * 0.7), s * rnd(0.25, 0.55), 0, -1e5,
        PShape.Fire, PFlag.Add | PFlag.Swirl, s * rnd(0.32, 0.46), s * rnd(0.2, 0.32), 0, C.flameHot, 1, C.flameCool, 0.8, 1.6, 1.5);
    }
    // dark lit smoke wound through the fire so the column has a silhouette
    for (let i = 0; i < this.n(18 * Math.min(2, dur)); i++) {
      P.spawn(x, gy + rnd(0, 1.5), z, s * rnd(0.25, 0.5), rnd(3, 5.5), rnd(4, 6), rnd(1.2, 1.8), rnd(0.05, dur * 0.7), s * 0.45, 0, -1e5,
        PShape.Puff, PFlag.Swirl | PFlag.Hot, s * 0.28, s * 0.55, rnd(-1, 1), C.smokeDark, 1, C.smokeDark, 1, 1, 0.9);
    }
    this.soft(x, gy + s * 0.8, z, s * 2.2, Math.min(dur, 1.2), C.flameHot, C.flameCool, 0.8);
    this.embers(x, gy + 1, z, this.n(16), 3, 1.8, C.ember, 3);
    this.scorch(x, gy, z, s * 0.9, 6);
  }

  private updateHazards(dt: number) {
    const P = this.particles, t = this.time, m = this.mul;
    for (let i = 0; i < MAX_HAZ; i++) {
      if (this.hId[i] < 0) continue;
      if (t > this.hEnd[i]) { this.hId[i] = -1; continue; }
      const x = this.hX[i], y = this.hY[i], z = this.hZ[i], R = this.hR[i];
      const left = this.hEnd[i] - t;
      const area = Math.min(4, (R * R) / 4);
      const fade = Math.min(1, left / 0.6);
      this.hAcc[i] += dt * m * (this.hEl[i] === El.Fire ? 1 : area) * fade;
      this.hAcc2[i] += dt * m * fade;
      switch (this.hEl[i]) {
        case El.Fire: {
          const per = 1 / Math.min(40, 7 * R * R + 6);
          while (this.hAcc[i] > per) {
            this.hAcc[i] -= per;
            this.flames(x, y, z, 1, R * 0.8, rnd(0.55, 1.05), 0.6, 0, 1.8);
            if (Math.random() < 0.3) this.embers(x + rnd(-R, R) * 0.5, y + 0.3, z + rnd(-R, R) * 0.5, 1, 0.6, 1.6);
          }
          while (this.hAcc2[i] > 1 / 3) {
            this.hAcc2[i] -= 1 / 3;
            const a = Math.random() * 6.283, rr = R * 0.5 * Math.random();
            this.puffs(x + Math.cos(a) * rr, y + 0.9, z + Math.sin(a) * rr, 1, 0.3, 0.4, 1.1, 2.0, 0, 1.0, C.smokeDark, true, 0.9, 1.4);
            this.soft(x, y + 0.5, z, R * 1.5, 0.5, C.flameHot, C.flameCool, 0.35);
            this.groundGlow(x, y, z, R * 1.9, 0.7, C.flameHot, C.flameCool, 0.4);
          }
          break;
        }
        case El.Gas: {
          // a low billowing cloud of lit mustard puffs that fills the radius, wisps curling off the
          // edge, and a thin ring on the ground that marks where the damage stops
          const per = 1 / Math.min(14, 2.2 * R * R + 3);
          while (this.hAcc[i] > per) {
            this.hAcc[i] -= per;
            const a = Math.random() * 6.283, rr = R * 0.85 * Math.sqrt(Math.random());
            P.spawn(x + Math.cos(a) * rr, y + rnd(0.2, 0.9), z + Math.sin(a) * rr, Math.cos(a) * 0.25, rnd(0.08, 0.3), Math.sin(a) * 0.25,
              rnd(2.0, 3.0), 0, -0.05, 0.8, y, PShape.Puff, 0, R * rnd(0.2, 0.3), R * rnd(0.4, 0.55), rnd(-0.4, 0.4), C.gas, 0.62, C.gasDeep, 0.4, 1, 0.95);
          }
          while (this.hAcc2[i] > 1 / 2) {
            this.hAcc2[i] -= 1 / 2;
            this.cards.spawn(CMode.Shock, COrient.Ground, x, y + DECAL_LIFT, z, 0, 0, 0, 0.62, 0, R, R, C.gas, 0.75, C.gasDeep, 0, 0.05, 0, 0, 0, 1);
          }
          break;
        }
        case El.Acid: {
          while (this.hAcc[i] > 1 / 16) {
            this.hAcc[i] -= 1 / 16;
            const a = Math.random() * 6.283, rr = R * 0.75 * Math.sqrt(Math.random());
            P.spawn(x + Math.cos(a) * rr, y + 0.06, z + Math.sin(a) * rr, 0, rnd(0.05, 0.2), 0, rnd(0.6, 1.1), 0, 0, 1, -1e5,
              PShape.Bubble, 0, rnd(0.1, 0.16), rnd(0.22, 0.36), 0, C.acidFoam, 1, C.acid, 1, 1, 1);
          }
          while (this.hAcc2[i] > 1 / 7) {
            this.hAcc2[i] -= 1 / 7;
            // rising wisps: thin soft fumes that stretch up and fade, not grey balls
            const a = Math.random() * 6.283, rr = R * 0.65 * Math.random();
            this.puffs(x + Math.cos(a) * rr, y + 0.25, z + Math.sin(a) * rr, 1, 0.15, 0.2, 0.75, 2.4, 0, 1.1, C.acidFume, false, 0.55, 1.2, -1e5, 0.8, PFlag.Soft);
          }
          break;
        }
        case El.Shock: {
          while (this.hAcc[i] > 1 / 9) {
            this.hAcc[i] -= 1 / 9;
            const a = Math.random() * 6.283, rr = R * 0.7 * Math.random();
            const x0 = x + Math.cos(a) * rr, z0 = z + Math.sin(a) * rr;
            const b = a + rnd(1.5, 3.5), rr2 = R * 0.8 * Math.random();
            this.cards.spawn(CMode.Arc, COrient.Segment, x0, y + 0.1, z0, x + Math.cos(b) * rr2, y + rnd(0.5, 1.5), z + Math.sin(b) * rr2,
              rnd(0.12, 0.2), 0, 0.6, 0, C.shock, 1, C.shockDeep, CFlag.Add, 0, Math.random() * 100, 0, 0, 2.6);
            if (Math.random() < 0.4) this.soft(x0, y + 0.4, z0, 1.6, 0.12, C.shock, C.shockDeep, 0.6);
            this.sparks(x0, y + 0.1, z0, 2, 3, C.shock, 2, 0.035, 0.2, 0.6, 6, y);
          }
          break;
        }
        case El.Ice: {
          while (this.hAcc2[i] > 1 / 2) {
            this.hAcc2[i] -= 1 / 2;
            const a = Math.random() * 6.283, rr = R * 0.7 * Math.random();
            this.puffs(x + Math.cos(a) * rr, y + 0.7, z + Math.sin(a) * rr, 1, 0.35, R * 0.3, R * 0.6, 2.0, 0, 0.1, C.ice, false, 0.7, 1.2, y, 0);
          }
          this.hAcc[i] = 0;
          break;
        }
        case El.Water: {
          while (this.hAcc[i] > 1 / 8) {
            this.hAcc[i] -= 1 / 8;
            const a = Math.random() * 6.283, rr = R * 0.7 * Math.random();
            const px = x + Math.cos(a) * rr, pz = z + Math.sin(a) * rr;
            this.cards.spawn(CMode.Shock, COrient.Ground, px, y + DECAL_LIFT + 0.02, pz, 0, 0, 0, 0.6, 0, 0.05, rnd(0.3, 0.6), C.ice, 0.8, C.water, 0, 0.12, 0, 0, 0, 1);
            for (let k = 0; k < 4; k++) P.spawn(px, y + 0.05, pz, rnd(-0.8, 0.8), rnd(1.5, 2.5), rnd(-0.8, 0.8), 0.4, 0, 12, 0.5, y,
              PShape.Drop, PFlag.Stretch, 0.04, 0.03, 0, C.water, 1, C.water, 1, 1.1, 1);
          }
          this.hAcc2[i] = 0;
          break;
        }
        case El.Steam: {
          while (this.hAcc[i] > 1 / 4) {
            this.hAcc[i] -= 1 / 4;
            const a = Math.random() * 6.283, rr = R * 0.6 * Math.random();
            this.puffs(x + Math.cos(a) * rr, y + 0.3, z + Math.sin(a) * rr, 1, 0.5, R * 0.3, R * 0.7, 2.2, 0, 0.9, C.steam, false, 0.85, 1.4, y, 0.4);
          }
          this.hAcc2[i] = 0;
          break;
        }
        case El.Tornado: {
          while (this.hAcc2[i] > 1 / 1.2) {
            this.hAcc2[i] -= 1 / 1.2;
            this.tornado(x, y, z, R, 0.9);
          }
          this.hAcc[i] = 0;
          break;
        }
      }
    }
  }

  private updateGate(dt: number) {
    if (!this.gOn) return;
    const c = this.gCharge, R = this.gR, P = this.particles;
    // the ring lies in the plane whose normal is (gNx, 0, gNz): basis t = (-gNz, 0, gNx), up = y
    const tx = -this.gNz, tz = this.gNx;
    this.gAcc += dt * this.mul * (10 + 50 * c);
    while (this.gAcc > 1) {
      this.gAcc -= 1;
      const a = Math.random() * 6.283, rr = R * rnd(1.05, 1.6);
      const ca = Math.cos(a), sa = Math.sin(a);
      const face = Math.random() < 0.5 ? -0.9 : 0.9; // swirl in on both faces, proud of the portal surface
      const ox = tx * ca * rr + this.gNx * face, oy = sa * rr, oz = tz * ca * rr + this.gNz * face;
      const k = 1.6, sw = 0.6; // drag, swirl: v/k lands near the centre
      const vx = (-tx * ca * rr + tx * -sa * rr * sw) * k, vy = (-oy + ca * rr * sw) * k, vz = (-tz * ca * rr + tz * -sa * rr * sw) * k;
      if (Math.random() < 0.5) P.spawn(this.gX + ox, this.gY + oy, this.gZ + oz, vx, vy, vz, rnd(1.0, 1.5), 0, 0, k, -1e5,
        PShape.Glow, PFlag.Add, 0.68 + 0.45 * c, 0.24, 0, C.sun, 1, C.amber, 0.5, 2.5 + 1.5 * c, 1.8);
      else P.spawn(this.gX + ox, this.gY + oy, this.gZ + oz, vx, vy, vz, rnd(1.0, 1.5), 0, 0, k, -1e5,
        PShape.Spark, PFlag.Stretch, 0.5 + 0.3 * c, 0.24, 0, C.sun, 1, C.amber, 0.7, 2.8, 2);
    }
    // the vortex: rotating spiral bands with ink between them and a warm gold rim, one card per face,
    // respawned every half life so the sin^2 envelopes sum flat (no persistent slot to lose to wrap)
    this.gAcc4 += dt;
    if (this.gAcc4 > 0.6) {
      this.gAcc4 = 0;
      for (let sd = -1; sd <= 1; sd += 2)
        this.cards.spawn(CMode.Vortex, COrient.Plane, this.gX + sd * this.gNx * 0.85, this.gY, this.gZ + sd * this.gNz * 0.85, this.gNx, 0, this.gNz,
          1.2, 0, R * 0.98, R * 0.98, C.sun, 0.35 + 0.65 * c, C.brassTrim, CFlag.Add, 0.25 + 0.5 * c, 0, 0, 0, 0.7 + 0.8 * c);
    }
    // the charge visibly builds: a vortex glow at the heart that grows and brightens with charge,
    // and a gold wash on the ground in front of the gate (fake light)
    this.gAcc3 += dt;
    if (this.gAcc3 > 0.3) {
      this.gAcc3 = 0;
      // on both faces, just proud of the portal surface (the world lane's disc writes depth)
      // heart glow, white bloom capped (critic 2: it was blowing out the whole ring)
      for (let sd = -1; sd <= 1; sd += 2) this.soft(this.gX + sd * this.gNx * 0.9, this.gY, this.gZ + sd * this.gNz * 0.9, R * (0.2 + 0.45 * c), 0.42, C.sun, C.amber, 0.35 + 1.0 * c);
      const g = this.ground(this.gX, this.gY, this.gZ);
      if (this.gY - g < R * 3) {
        // the wash spills off the dais onto the street in front of (and behind) the ring
        for (let sd = -1; sd <= 1; sd += 2) {
          const wx = this.gX + sd * this.gNx * R * 1.1, wz = this.gZ + sd * this.gNz * R * 1.1;
          this.groundGlow(wx, this.ground(wx, g + 1, wz), wz, R * (0.55 + 0.5 * c), 0.42, C.sun, C.amber, 0.4 + 1.2 * c);
        }
      }
    }
    if (c > 0.95) {
      this.gAcc2 += dt;
      if (this.gAcc2 > 0.45) {
        this.gAcc2 = 0;
        this.soft(this.gX, this.gY, this.gZ, R * 0.7, 0.6, C.sun, C.amber, 0.45);
        this.embers(this.gX, this.gY - R * 0.9, this.gZ, this.n(6), 1.5, 2, C.sun, 3);
      }
    }
  }

  update(dt: number, time: number, camera: THREE.Camera) {
    this.time = time;
    this.updateHazards(dt);
    this.updateGate(dt);
    this.particles.update(time);
    this.cards.update(time);
    this.debris.update(time);
    this.numbers.update(time, camera);
    let pxPerM = 600;
    const cam = camera as THREE.PerspectiveCamera;
    if (cam.position) { this.camX = cam.position.x; this.camY = cam.position.y; this.camZ = cam.position.z; }
    this.water.update(time, this.camX, this.camY, this.camZ);
    if (this.renderer && cam.projectionMatrix) {
      this.renderer.getDrawingBufferSize(this.tmp2);
      pxPerM = this.tmp2.y * 0.5 * cam.projectionMatrix.elements[5];
    }
    this.bulletView.update(time, pxPerM);
    const s = this.screen;
    s.flash *= Math.exp(-dt * 9);
    s.chroma *= Math.exp(-dt * 6);
    s.hurt *= Math.exp(-dt * 3.5);
    s.shake *= Math.exp(-dt * 6);
  }

  dispose() {
    for (const m of [this.particles.mesh, this.cards.mesh, this.debris.mesh, this.numbers.mesh, this.water.mesh]) {
      m.parent?.remove(m); m.geometry.dispose(); (m.material as THREE.Material).dispose();
    }
    this.bulletView.group.parent?.remove(this.bulletView.group);
  }
}

const SHOCK_FLASH = [0.7, 0.9, 1.0];
const linMag = linRGB(0x8a1050);
const goldBright = linRGB(0xffd35a);
const navy = linRGB(0x1d2f4a);
const greyArmor = linRGB(0xb9b4ae);
